import React, { useState, useEffect } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { apiClient } from '../services/api';
import { opexApi } from '../services/opexApi';
import { convertMonthlyOpexToData } from '../utils/opexUtils';
import { cashFlowManagementApi, CashFlowManagementData } from '../services/cashFlowManagementApi';
import { employeeApi } from '../services/employeeApi';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';

// Cash Flow 를 운영한 첫 연도. 이보다 이전 연도는 선택할 수 없다.
//
// 2025 는 cash_flow_management 에 행만 있고 편집 데이터(editable_data·opex_data)가
// 비어 있는 껍데기다. 반면 OPEX 는 year 테이블에 2025 가 없어서, 2025 를 열면
// 백엔드가 연도 자동 초기화를 시도하다 실패해 400 을 낸다.
// 그런데 fetchYearData 가 그 실패를 삼키고 0 을 반환하므로, 화면에는 에러 대신
// OPEX·인원수·Project Expense 가 전부 0원인 정상 화면처럼 보인다.
// 자세한 경위: grkcon-corp/GRK_Workspace#25
const CASH_FLOW_START_YEAR = 2026;

// RowData (generateDynamicData에서 사용)
interface RowData {
    type: 'header' | 'total' | 'section' | 'item' | 'spacer';
    label?: string;
    indent?: number;
    values?: number[];
    sectionId?: string; // 행이 속한 섹션 (b-1, b-2, research, consulting 등)
}

interface Employee {
    id: number;
    name: string;
    email: string;
    position: string;
    department: string;
    joinDate: string;
    endDate?: string;
    status: string;
    annualSalary: string;
}


// ===== Helper Functions =====

// 통화 포맷 (만원 단위, 음수는 그대로 유지)
const formatCurrency = (value: number | null | undefined): string => {
    if (typeof value !== 'number' || isNaN(value)) return '0';
    const converted = Math.round(value / 10000);
    return new Intl.NumberFormat('ko-KR').format(converted);
};

// 소계 계산 (12개월 합계)
const calculateSubtotal = (values: number[]): number => {
    return values.reduce((sum, val) => sum + (val || 0), 0);
};

// ===== 초기 데이터 구조 (generateDynamicData 호환용) =====
const initialFinancialData: { header: string[]; rows: RowData[] } = {
    header: ['항목', '소계', '1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
    rows: []
};

// ===== 라벨 매핑 (영문 키 → 한글 표시명) =====
const LABEL_MAP: Record<string, string> = {
    // Level 2 카테고리
    'Annual Salary': 'b-1. 인건비',
    'Out sourcing & Restructuring': 'b-2. 외주인건비',
    // Level 3 항목
    'indirect opex': '법인운영비용',
    'Summer Bonus & Welfare': '복지비용',
    'Project Expense': '프로젝트 운영비용',
    'direct opex': '파트너 비용',
    'T & E Expense': '출장비',
    '01. Research Consulting': '연구매출',
    '02. CRO & Grants': '투자매출',
    '03. BPO (manuscription)': 'BPO',
    '연구부분': '연구 사업부',
    '컨설팅부분': '컨설팅 사업부',
};

// 라벨 변환 함수
const getDisplayLabel = (key: string): string => LABEL_MAP[key] || key;

// ===== 숫자 배열 정리 함수 (손상된 데이터 방지) =====
const sanitizeNumberArray = (arr: any): number[] => {
    if (!Array.isArray(arr)) return new Array(12).fill(0);
    return arr.map(val => {
        const num = Number(val);
        // NaN, Infinity, 또는 비정상적으로 큰 숫자(1000조 초과)는 0으로 처리
        return isNaN(num) || !isFinite(num) || Math.abs(num) > 1e15 ? 0 : num;
    });
};

// ===== 부서 순서 상수 =====
const DEPT_ORDER: Record<string, number> = {
    '대표이사': 1,
    '컨설팅팀': 2, '컨설팅': 2, '컨설팅1': 2, '컨설팅2': 2, '컨설팅3': 2,
    '연구팀': 3, '연구': 3,
    '경영': 4, '경영지원팀': 4, '경영지원': 4,
    'IT': 5, 'IT팀': 5
};

// ===== 직급 순서 상수 =====
const POSITION_ORDER: Record<string, number> = {
    '대표이사': 1, '상무': 2, 'Principal': 3, 'Director': 4, 'VP': 5,
    'Senior Manager': 6, 'Manager': 7, 'Senior': 8, 'Staff': 9,
    'Senior Associate': 10, 'Associate': 11, 'Business Analyst': 12, 'RA (인턴)': 13,
    '이사': 4, '부장': 5, '차장': 6, '과장': 7, '대리': 8, '사원': 9,
    'Senior Business Analyst': 10
};

// ===== 연구 라벨 키 맵핑 상수 =====
const RESEARCH_KEY_MAP: Record<string, 'researchConsulting' | 'croGrants' | 'bpoManuscription'> = {
    '01. Research Consulting': 'researchConsulting',
    '02. CRO & Grants': 'croGrants',
    '03. BPO (manuscription)': 'bpoManuscription'
};

// --- Main CashFlow Component ---
const CashFlow: React.FC = () => {
    const { userRole } = useAuth();
    const { showToast } = useAlert();
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [dynamicFinancialData, setDynamicFinancialData] = useState(initialFinancialData);

    const [combinedHRCostData, setCombinedHRCostData] = useState<Record<string, any> | null>(null);
    const [editableData, setEditableData] = useState<{[key: string]: number[]}>({
        'Summer Bonus & Welfare': new Array(12).fill(0),
        'Project Expense': new Array(12).fill(0),
        'T & E Expense': new Array(12).fill(0),
        '기말현금': new Array(12).fill(0),
        '기초현금': new Array(12).fill(0)
    });
    const [researchAdditionalItems, setResearchAdditionalItems] = useState<{label: string, values: number[]}[]>([]);
    
    // Cash Flow 전용 추가 직원들 (트리거가 아닌 수동 추가)
    const [cfAdditionalEmployees, setCfAdditionalEmployees] = useState<{id?: string, name: string, monthlyCosts: number[]}[]>([]);
    
    // 연구부분 항목들
    const [researchItems, setResearchItems] = useState({
        '01. Research Consulting': new Array(12).fill(0),
        '02. CRO & Grants': new Array(12).fill(0),
        '03. BPO (manuscription)': new Array(12).fill(0)
    });

    // 연구부분 표시명 (수정 가능)
    const [researchDisplayLabels, setResearchDisplayLabels] = useState({
        researchConsulting: '연구매출',
        croGrants: '투자매출',
        bpoManuscription: 'BPO'
    });
    // 연구부분 표시명 인라인 편집 상태
    const [editingLabelKey, setEditingLabelKey] = useState<keyof typeof researchDisplayLabels | null>(null);
    const [editingLabelValue, setEditingLabelValue] = useState('');

    // PPE 기반 동적 컨설팅 프로젝트들
    const [consultingDynamicProjects, setConsultingDynamicProjects] = useState<{[key: string]: number[]}>({});

    // ===== 접기/펼치기 상태 =====
    const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

    // 섹션 접기/펼치기 토글
    const toggleSection = (sectionId: string) => {
        setCollapsedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionId)) {
                newSet.delete(sectionId);
            } else {
                newSet.add(sectionId);
            }
            return newSet;
        });
    };

    const [isEditing, setIsEditing] = useState<{rowLabel: string, monthIndex: number, sectionId: string | null} | null>(null);
    const [editValue, setEditValue] = useState<string>('');
    const currentYear = new Date().getFullYear();

    // 연도 선택을 위한 상태 (sessionStorage에서 복원)
    const [selectedYear, setSelectedYear] = useState(() => {
        const savedYear = sessionStorage.getItem('cashFlowSelectedYear');
        const restored = savedYear ? parseInt(savedYear) : currentYear;
        // 드롭다운에서 뺐어도 이전 세션 값이 남아 있으면 그대로 조회되므로 여기서도 막는다
        return Number.isNaN(restored) || restored < CASH_FLOW_START_YEAR
            ? Math.max(currentYear, CASH_FLOW_START_YEAR)
            : restored;
    });
    const [availableYears, setAvailableYears] = useState<number[]>([currentYear]);

    // 연도를 유지하면서 페이지 reload하는 함수
    const reloadWithYear = () => {
        sessionStorage.setItem('cashFlowSelectedYear', selectedYear.toString());
        window.location.reload();
    };

    // CF 전용 직원 추가 함수 (연구 사업부와 동일한 전체 저장 방식)
    const addCfEmployee = async () => {
        const employeeName = prompt('직원 이름을 입력하세요:');
        if (employeeName && employeeName.trim()) {
            const trimmedName = employeeName.trim();

            // 중복 체크 1: CF 추가 직원 내 중복 확인
            const existingCfEmployee = cfAdditionalEmployees.find(emp => emp.name === trimmedName);
            if (existingCfEmployee) {
                showToast('이미 같은 이름의 CF 직원이 존재합니다.', 'error');
                return;
            }

            // 중복 체크 2: 일반 직원(트리거로 관리되는 직원) 내 중복 확인
            const existingEmployee = employees.find(emp => emp.name === trimmedName);
            if (existingEmployee) {
                showToast('일반 직원 목록에 같은 이름의 직원이 이미 존재합니다.\nCF 직원은 일반 직원과 다른 이름을 사용해주세요.', 'error');
                return;
            }

            try {
                // 로컬 state 업데이트
                const newEmployee = {
                    id: `cf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    name: trimmedName,
                    monthlyCosts: new Array(12).fill(0)
                };
                const updatedCfEmployees = [...cfAdditionalEmployees, newEmployee];
                setCfAdditionalEmployees(updatedCfEmployees);

                // 화면 재생성
                await generateDynamicData(researchAdditionalItems, editableData, researchItems, null);

                // 전체 저장 (cfAdditionalEmployees 포함)
                await saveCashFlowData(editableData, researchItems, researchAdditionalItems, updatedCfEmployees);

                showToast('직원이 추가되었습니다.', 'success');
                setTimeout(() => reloadWithYear(), 100);
            } catch (error) {
                showToast('직원 추가에 실패했습니다.', 'error');
            }
        }
    };

    // CF 전용 직원 삭제 함수 (연구 사업부와 동일한 전체 저장 방식)
    const removeCfEmployee = async (employeeIdOrName: string) => {
        try {
            // 로컬 state에서 제거
            const updatedCfEmployees = cfAdditionalEmployees.filter(emp =>
                emp.id !== employeeIdOrName && emp.name !== employeeIdOrName
            );
            setCfAdditionalEmployees(updatedCfEmployees);

            // 화면 재생성
            await generateDynamicData(researchAdditionalItems, editableData, researchItems, null);

            // 전체 저장 (cfAdditionalEmployees 포함)
            await saveCashFlowData(editableData, researchItems, researchAdditionalItems, updatedCfEmployees);

            showToast('직원이 삭제되었습니다.', 'success');
            setTimeout(() => reloadWithYear(), 100);
        } catch (error) {
            showToast('직원 삭제에 실패했습니다.', 'error');
        }
    };

    // 퇴사한 직원의 해당 월이 퇴사월 이후인지 확인하는 함수 (노랑색 배경 적용용)
    const isResignedEmployeeInactiveMonth = (employee: Employee, year: number, month: number): boolean => {
        // 퇴사하지 않은 직원은 해당없음
        if (employee.status === 'ACTIVE' || !employee.endDate) return false;
        
        const endDate = new Date(employee.endDate);
        const endYear = endDate.getFullYear();
        const endMonth = endDate.getMonth() + 1; // getMonth()는 0-11이므로 +1
        
        // 퇴사년도보다 이후 연도이거나, 같은 연도에서 퇴사월 이후인 경우
        if (year > endYear || (year === endYear && month > endMonth)) {
            return true;
        }
        
        return false;
    };


    // OPEX 데이터를 연간 단위로 가져오는 함수 (성능 최적화)
    const fetchYearData = async (year: number): Promise<{indirect: number[], direct: number[], employeeCounts: number[]}> => {
        try {
            const yearlyOpex = await opexApi.getByYear(year);
            const indirect = new Array(12).fill(0);
            const direct = new Array(12).fill(0);
            const employeeCounts = new Array(12).fill(0);
            
            // OPEX Management와 동일한 변환 및 계산 로직
            const calculateCategoryTotal = (category: any): number => {
                if (!category.children || category.children.length === 0) {
                    return Number(category.amount || 0);
                }
                return category.children.reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0);
            };

            const calculateHierarchicalTotal = (items: any[]): number => {
                return items.reduce((sum, category) => {
                    return sum + calculateCategoryTotal(category);
                }, 0);
            };
            
            // 12개월 데이터를 한번에 처리
            for (let month = 1; month <= 12; month++) {
                const rawMonthData = yearlyOpex.months.find(m => m.month === month);
                if (rawMonthData) {
                    const convertedMonthData = convertMonthlyOpexToData(rawMonthData);
                    indirect[month - 1] = calculateHierarchicalTotal(convertedMonthData.indirect);
                    direct[month - 1] = calculateHierarchicalTotal(convertedMonthData.direct);
                    employeeCounts[month - 1] = rawMonthData.employeeCount || 0;
                }
            }
            
            return { indirect, direct, employeeCounts };
        } catch (error) {
            return {
                indirect: new Array(12).fill(0),
                direct: new Array(12).fill(0),
                employeeCounts: new Array(12).fill(0)
            };
        }
    };


    // PPE 프로젝트 매칭 함수는 더 이상 필요 없음 (동적 생성으로 변경됨)

    // 동적 데이터 생성 함수 (성능 최적화된 버전)
    const generateDynamicData = async (customResearchItems?: typeof researchAdditionalItems, preloadedEditableData?: any, preloadedResearchItems?: any, savedData?: any) => {
        const itemsToUse = customResearchItems || researchAdditionalItems;
        try {
            const currentEditableData = preloadedEditableData || { ...editableData };
            const currentResearchItems = preloadedResearchItems || { ...researchItems };

            // OPEX 데이터는 항상 실시간으로 가져오기 (Project Expense 계산용)
            const yearlyOpexData = await fetchYearData(selectedYear);
            const employeeCountsFromOpex = yearlyOpexData.employeeCounts;

            // Project Expense 자동 계산: OPEX의 employeeCount * 75만원
            let projectExpenseValues = new Array(12);

            for (let month = 0; month < 12; month++) {
                const employeeCount = employeeCountsFromOpex[month] || 0;
                projectExpenseValues[month] = employeeCount * 750000;
            }

            // Project Expense를 editableData에 설정
            currentEditableData['Project Expense'] = projectExpenseValues;

            // React state 업데이트
            setEditableData(prev => ({
                ...prev,
                'Project Expense': projectExpenseValues
            }));

            // savedData가 있으면 트리거 기반 데이터 사용 (간단한 방식)
            if (savedData) {
                const newRows: RowData[] = [];

                // ===== A. 현금흐름 섹션 =====
                // A. 현금흐름 Level 1 (총계는 기말현금 - 기초현금으로 계산)
                const endingCashValues = savedData.editableData?.endingCash || new Array(12).fill(0);
                const beginningCashValues = savedData.editableData?.beginningCash || new Array(12).fill(0);
                const cashFlowTotals = endingCashValues.map((val: number, idx: number) => val - beginningCashValues[idx]);
                newRows.push({
                    type: 'section' as const,
                    label: 'A. 현금흐름',
                    values: cashFlowTotals
                });

                // a-1. 기말현금
                newRows.push({
                    type: 'header' as const,
                    label: '기말현금',
                    values: endingCashValues
                });
                // a-2. 기초현금
                newRows.push({
                    type: 'header' as const,
                    label: '기초현금',
                    values: beginningCashValues
                });

                // ===== B. 비용 총계 섹션 =====
                // 지출합계 - 항상 실시간 계산 (저장된 값 사용하지 않음)
                newRows.push({
                    type: 'total' as const,
                    label: '지출합계',
                    values: new Array(12).fill(0) // 초기값, 나중에 실시간 계산으로 업데이트됨
                });
                
                // Annual Salary 총계 계산 (저장된 데이터 기준)
                const annualSalaryTotals = new Array(12).fill(0);
                // Annual Salary 계산 시작

                // 일반 직원들 계산
                if (savedData.annualSalaryData?.employees) {
                    for (let monthIdx = 0; monthIdx < 12; monthIdx++) {
                        let monthTotal = 0;
                        savedData.annualSalaryData.employees.forEach((emp: any) => {
                            const empCost = Math.round(emp.monthlyCosts[monthIdx] || 0);
                            monthTotal += empCost;
                            // 월별 비용 계산
                        });
                        annualSalaryTotals[monthIdx] += monthTotal;
                        // 월별 총합 계산
                    }
                }

                // CF 전용 직원들은 b-2. 외주인건비 하위에 표시되므로 Annual Salary 총계에서 제외
                // Annual Salary 총계 계산 완료
                newRows.push({ type: 'total' as const, label: 'Annual Salary', values: annualSalaryTotals });
                
                // 직원별 상세 데이터 (정렬 적용)
                if (savedData.annualSalaryData?.employees) {
                    // 실제 직원 정보 가져와서 매칭
                    const allEmployees = await employeeApi.getAll(userRole);

                    // 직원 데이터를 Employee 형식으로 변환하고 정렬
                    const convertedEmployees = savedData.annualSalaryData.employees.map((emp: any) => {
                        // ID로 실제 직원 정보 찾기
                        const realEmployee = allEmployees.find((realEmp: any) => realEmp.id === emp.id);

                        return {
                            id: emp.id,
                            name: emp.name,
                            email: realEmployee?.email || '',
                            position: realEmployee?.position || '',
                            department: realEmployee?.department || '',
                            joinDate: realEmployee?.joinDate || '',
                            endDate: realEmployee?.endDate || undefined,
                            status: realEmployee?.endDate ? 'RESIGNED' : 'ACTIVE',
                            annualSalary: realEmployee?.annualSalary || '0',
                            monthlyCosts: emp.monthlyCosts
                        };
                    });
                    
                    // 직원 정렬: 윤승현 최우선 → 재직/퇴사 → 부서 순서 → 직급 순서 → 이름 순서
                    const sortedEmployees = convertedEmployees.sort((a: any, b: any) => {
                        // 1. 윤승현 최우선
                        if (a.name === '윤승현') return -1;
                        if (b.name === '윤승현') return 1;

                        // 2. 재직자 우선, 퇴사자 아래로
                        const aIsActive = !a.endDate || a.endDate === null;
                        const bIsActive = !b.endDate || b.endDate === null;
                        if (aIsActive && !bIsActive) return -1;
                        if (!aIsActive && bIsActive) return 1;

                        // 3. 부서 순서
                        const aDeptOrder = DEPT_ORDER[a.department] || 99;
                        const bDeptOrder = DEPT_ORDER[b.department] || 99;
                        if (aDeptOrder !== bDeptOrder) return aDeptOrder - bDeptOrder;

                        // 4. 직급 순서
                        const aPosOrder = POSITION_ORDER[a.position] || 99;
                        const bPosOrder = POSITION_ORDER[b.position] || 99;
                        if (aPosOrder !== bPosOrder) return aPosOrder - bPosOrder;

                        // 5. 이름 순서
                        return a.name.localeCompare(b.name);
                    });
                    
                    // 퇴사자 스타일링을 위한 직원 데이터 매핑 생성
                    const employeeDataForStyling: Record<string, any> = {};
                    sortedEmployees.forEach((employee: any) => {
                        employeeDataForStyling[employee.name] = { employee };
                    });
                    setCombinedHRCostData(employeeDataForStyling);
                    setEmployees(sortedEmployees);

                    // 정렬된 직원들을 테이블에 추가
                    sortedEmployees.forEach((emp: any) => {
                        newRows.push({
                            type: 'item' as const,
                            label: emp.name,
                            indent: 1,
                            values: emp.monthlyCosts
                        });
                    });

                }

                // ===== b-2. 외주인건비 (CF 직원 합계로 자동 계산) =====
                // 먼저 CF 직원들의 합계 계산
                const outSourcingTotals = new Array(12).fill(0);
                if (savedData.cfAdditionalEmployees && savedData.cfAdditionalEmployees.length > 0) {
                    savedData.cfAdditionalEmployees.forEach((cfEmp: any) => {
                        cfEmp.monthlyCosts?.forEach((cost: number, idx: number) => {
                            outSourcingTotals[idx] += Math.round(cost || 0);
                        });
                    });
                }

                // b-2 총계 행 (자동 계산된 값)
                newRows.push({
                    type: 'total' as const,
                    label: 'Out sourcing & Restructuring',
                    values: outSourcingTotals
                });

                // CF 전용 추가 직원들 (외주인건비 하위)
                if (savedData.cfAdditionalEmployees && savedData.cfAdditionalEmployees.length > 0) {
                    savedData.cfAdditionalEmployees.forEach((cfEmp: any) => {
                        newRows.push({
                            type: 'item' as const,
                            label: cfEmp.name,
                            indent: 1,
                            values: cfEmp.monthlyCosts,
                            sectionId: 'b-2' // 외주인건비 섹션 표시
                        });
                    });
                }

                // ===== b-3. 고정비 섹션 =====
                const indirectOpexValues = savedData.opexData?.indirectOpex || new Array(12).fill(0);
                const summerBonusValues = savedData.summerBonusWelfare || new Array(12).fill(0);
                const fixedCostTotals = indirectOpexValues.map((val: number, idx: number) => val + summerBonusValues[idx]);
                newRows.push({
                    type: 'section' as const,
                    label: 'b-3. 고정비',
                    values: fixedCostTotals
                });
                // 법인운영비용
                newRows.push({
                    type: 'item' as const,
                    label: 'indirect opex',
                    indent: 1,
                    values: indirectOpexValues
                });
                // 복지비용
                newRows.push({
                    type: 'item' as const,
                    label: 'Summer Bonus & Welfare',
                    indent: 1,
                    values: summerBonusValues
                });

                // ===== b-4. 변동비 섹션 =====
                const directOpexValues = savedData.opexData?.directOpex || new Array(12).fill(0);
                const tAndEValues = savedData.tAndEExpense || new Array(12).fill(0);
                const variableCostTotals = projectExpenseValues.map((val: number, idx: number) =>
                    val + directOpexValues[idx] + tAndEValues[idx]
                );
                newRows.push({
                    type: 'section' as const,
                    label: 'b-4. 변동비',
                    values: variableCostTotals
                });
                // 프로젝트 운영비용
                newRows.push({
                    type: 'item' as const,
                    label: 'Project Expense',
                    indent: 1,
                    values: projectExpenseValues
                });
                // 파트너 비용
                newRows.push({
                    type: 'item' as const,
                    label: 'direct opex',
                    indent: 1,
                    values: directOpexValues
                });
                // 출장비
                newRows.push({
                    type: 'item' as const,
                    label: 'T & E Expense',
                    indent: 1,
                    values: tAndEValues
                });

                newRows.push({ type: 'spacer' as const });

                // ===== C. 매출 총계 섹션 =====
                
                // 총 매출은 연구부분 + 컨설팅부분 합계로 나중에 계산될 예정
                const totalRevenueValues = new Array(12).fill(0);

                // 연구부분 - 월별 합계 계산
                const researchTotals = new Array(12).fill(0);

                // 연구 고정 항목들 합계 계산
                if (savedData.researchItems?.fixedItems) {
                    // 각 고정 항목의 합계 추가
                    ['researchConsulting', 'croGrants', 'bpoManuscription'].forEach(key => {
                        const values = savedData.researchItems.fixedItems[key] || new Array(12).fill(0);
                        values.forEach((val: number, idx: number) => {
                            researchTotals[idx] += val || 0;
                        });
                    });
                }

                // 연구 추가 항목들 합계 계산
                if (savedData.researchItems?.additionalItems) {
                    savedData.researchItems.additionalItems.forEach((item: any) => {
                        item.values.forEach((val: number, idx: number) => {
                            researchTotals[idx] += val || 0;
                        });
                    });
                }

                // 연구부분 섹션 헤더에 합계 표시
                newRows.push({ type: 'section' as const, label: '연구부분', values: researchTotals });

                // 연구 고정 항목들 개별 추가
                if (savedData.researchItems?.fixedItems) {
                    newRows.push({
                        type: 'item' as const,
                        label: '01. Research Consulting',
                        indent: 1,
                        values: savedData.researchItems.fixedItems.researchConsulting || new Array(12).fill(0)
                    });
                    newRows.push({
                        type: 'item' as const,
                        label: '02. CRO & Grants',
                        indent: 1,
                        values: savedData.researchItems.fixedItems.croGrants || new Array(12).fill(0)
                    });
                    newRows.push({
                        type: 'item' as const,
                        label: '03. BPO (manuscription)',
                        indent: 1,
                        values: savedData.researchItems.fixedItems.bpoManuscription || new Array(12).fill(0)
                    });
                }

                // 연구 추가 항목들 개별 추가
                if (savedData.researchItems?.additionalItems) {
                    savedData.researchItems.additionalItems.forEach((item: any) => {
                        newRows.push({
                            type: 'item' as const,
                            label: item.label,
                            indent: 1,
                            values: item.values,
                            sectionId: 'research' // 연구 사업부 섹션 표시
                        });
                    });
                }

                // 컨설팅부분 - 초기값은 0으로 설정 후 하위 항목 합계로 업데이트
                const consultingTotals = new Array(12).fill(0);

                // 컨설팅 프로젝트들의 합계 계산
                if (savedData.consultingDynamicProjects) {
                    Object.entries(savedData.consultingDynamicProjects).forEach(([, monthlyRevenue]: [string, any]) => {
                        monthlyRevenue.forEach((val: number, monthIndex: number) => {
                            consultingTotals[monthIndex] += val || 0;
                        });
                    });
                }

                // 컨설팅부분 섹션 헤더에 합계 표시
                newRows.push({ type: 'section' as const, label: '컨설팅부분', values: consultingTotals });

                // 컨설팅 프로젝트들 개별 추가
                if (savedData.consultingDynamicProjects) {
                    Object.entries(savedData.consultingDynamicProjects).forEach(([projName, monthlyRevenue]: [string, any]) => {
                        newRows.push({
                            type: 'item' as const,
                            label: projName,
                            indent: 1,
                            values: monthlyRevenue
                        });
                    });
                }

                // 총 매출 = 연구부분 + 컨설팅부분
                for (let i = 0; i < 12; i++) {
                    totalRevenueValues[i] = researchTotals[i] + consultingTotals[i];
                }

                // 총 매출 행을 맨 앞에 추가 (연구부분 앞)
                const totalRevenueIndex = newRows.findIndex(row => row.label === '연구부분');
                newRows.splice(totalRevenueIndex, 0, {
                    type: 'section' as const,
                    label: '총 매출',
                    values: totalRevenueValues
                });

                // savedData가 있는 경우 지출합계 재계산 후 비교 로직 실행
                const finalUpdatedRows = newRows;

                // 지출합계 재계산
                const rowsWithRecalculatedExpenseTotal = finalUpdatedRows.map(row => {
                    if (row.label === '지출합계') {
                        const 지출합계Values = new Array(12).fill(0);

                        const annualSalaryRow = finalUpdatedRows.find(r => r.label === 'Annual Salary');
                        const summerBonusRow = finalUpdatedRows.find(r => r.label === 'Summer Bonus & Welfare');
                        const projectExpenseRow = finalUpdatedRows.find(r => r.label === 'Project Expense');
                        const teExpenseRow = finalUpdatedRows.find(r => r.label === 'T & E Expense');
                        const indirectOpexRow = finalUpdatedRows.find(r => r.label === 'indirect opex');
                        const directOpexRow = finalUpdatedRows.find(r => r.label === 'direct opex');
                        const outSourcingRow = finalUpdatedRows.find(r => r.label === 'Out sourcing & Restructuring');

                        for (let idx = 0; idx < 12; idx++) {
                            지출합계Values[idx] =
                                (Number(annualSalaryRow?.values?.[idx]) || 0) +
                                (Number(summerBonusRow?.values?.[idx]) || 0) +
                                (Number(projectExpenseRow?.values?.[idx]) || 0) +
                                (Number(teExpenseRow?.values?.[idx]) || 0) +
                                (Number(indirectOpexRow?.values?.[idx]) || 0) +
                                (Number(directOpexRow?.values?.[idx]) || 0) +
                                (Number(outSourcingRow?.values?.[idx]) || 0);
                        }

                        return { ...row, values: 지출합계Values };
                    }
                    return row;
                });

                // 📊 DB의 calculated_totals와 현재 지출합계 비교하여 자동 재계산 판단 (savedData 경우)
                const shouldRecalculate = await (async () => {
                    try {
                        const dbExpenseTotals = savedData.calculatedTotals?.monthlyTotals || [];
                        const currentExpenseRow = rowsWithRecalculatedExpenseTotal.find(row => row.label === '지출합계');
                        const currentExpenseTotals = currentExpenseRow?.values || [];

                        // 합계가 다르면 재계산 필요
                        const differences = dbExpenseTotals.map((dbTotal: number, index: number) => {
                            const currentTotal = currentExpenseTotals[index] || 0;
                            const diff = Math.abs((dbTotal || 0) - currentTotal);
                            return { month: index + 1, db: dbTotal || 0, current: currentTotal, diff };
                        });

                        const isDifferent = differences.some((d: any) => d.diff > 0.01);

                        return isDifferent;
                    } catch (error) {
                        return false;
                    }
                })();

                // 지출합계 재계산 후 기말현금도 자동 재계산
                // customResearchItems가 전달되면 연구부분 추가/삭제이므로 항상 재계산
                const needsRecalculation = shouldRecalculate || !!customResearchItems;
                const finalRowsWithEndingCash = needsRecalculation
                    ? recalculateEndingCash(rowsWithRecalculatedExpenseTotal) // 1월부터 전체 재계산
                    : rowsWithRecalculatedExpenseTotal; // 재계산 없이 그대로 사용

                setDynamicFinancialData({
                    ...initialFinancialData,
                    rows: finalRowsWithEndingCash
                });

                // 재계산된 기말현금, 기초현금 값을 editableData에 반영 (확정 버튼으로 저장)
                if (needsRecalculation) {
                    setTimeout(() => {
                        const 기말현금Row = finalRowsWithEndingCash.find(row => row.label === '기말현금');
                        const 기초현금Row = finalRowsWithEndingCash.find(row => row.label === '기초현금');

                        setEditableData(prev => ({
                            ...prev,
                            '기말현금': 기말현금Row?.values || prev['기말현금'],
                            '기초현금': 기초현금Row?.values || prev['기초현금']
                        }));
                    }, 200);
                }

                return; // savedData 처리 완료
            }
            
            // savedData가 없으면 실시간 데이터 사용 (기존 로직)
            const employeesFromSavedData: any[] = savedData?.annualSalaryData?.employees || [];
            // generateDynamicData 데이터 소스 확인
            
            // 직원 데이터 처리: savedData가 있으면 트리거 데이터 사용, 없으면 실시간 데이터 사용
            let sortedEmployees: any[] = [];
            
            if (savedData && employeesFromSavedData.length > 0) {
                // 트리거 기반 데이터 사용 (저장된 데이터 조회 시)
                const convertedEmployees = employeesFromSavedData.map((emp: any) => ({
                    id: emp.id,
                    name: emp.name,
                    email: '',
                    position: emp.position || '',
                    department: emp.department || '',
                    joinDate: emp.joinDate || '',
                    endDate: emp.endDate || undefined,
                    status: 'ACTIVE',
                    annualSalary: '0'
                }));
                sortedEmployees = convertedEmployees;
            } else {
                // savedData가 없으면 실시간으로 직원 데이터 조회
                // 실시간 직원 데이터 조회
                const allEmployees = await employeeApi.getAll(userRole);
                sortedEmployees = allEmployees;
            }
            
            // 직원 정렬 (savedData 여부와 관계없이 항상 실행): 윤승현 최우선 → 재직/퇴사 → 부서 순서 → 직급 순서 → 이름 순서
            sortedEmployees = sortedEmployees.sort((a: any, b: any) => {
                // 1. 윤승현 최우선
                if (a.name === '윤승현') return -1;
                if (b.name === '윤승현') return 1;

                // 2. 재직자 우선, 퇴사자 아래로
                const aIsActive = !a.endDate || a.endDate === null;
                const bIsActive = !b.endDate || b.endDate === null;
                if (aIsActive && !bIsActive) return -1;
                if (!aIsActive && bIsActive) return 1;

                // 3. 부서 순서
                const aDeptOrder = DEPT_ORDER[a.department] || 99;
                const bDeptOrder = DEPT_ORDER[b.department] || 99;
                if (aDeptOrder !== bDeptOrder) return aDeptOrder - bDeptOrder;

                // 4. 직급 순서
                const aPosOrder = POSITION_ORDER[a.position] || 99;
                const bPosOrder = POSITION_ORDER[b.position] || 99;
                if (aPosOrder !== bPosOrder) return aPosOrder - bPosOrder;

                // 5. 이름 순서
                return a.name.localeCompare(b.name);
            });
            
            setEmployees(sortedEmployees);

            // 직원별 월부담액 직접 계산
            const annualSalaryRows: RowData[] = [];
            const annualSalaryTotals = new Array(12).fill(0);

            // 퇴사자 스타일링을 위한 직원 데이터 매핑 생성
            const employeeDataForStyling: Record<string, any> = {};
            sortedEmployees.forEach((employee: any) => {
                employeeDataForStyling[employee.name] = { employee };
            });
            setCombinedHRCostData(employeeDataForStyling);

            // 실시간 데이터 사용 시 HR Cost 데이터 한 번에 조회
            let allHRCostData: any[] = [];
            if (!savedData || employeesFromSavedData.length === 0) {
                // 실시간 HR Cost 데이터 조회
                try {
                    const response = await fetch(`/api/employees/hr-cost-yearly/${selectedYear}`);
                    allHRCostData = await response.json();
                    // HR Cost 데이터 조회 완료
                } catch (error) {
                    // HR Cost 조회 실패
                    allHRCostData = [];
                }
            }

            // 먼저 각 직원의 월별 부담액 계산 (PS 계산 전)
            const employeeHRDataPreCalc = sortedEmployees.map((employee: any) => {
                // 직원 데이터 처리
                if (savedData && employeesFromSavedData.length > 0) {
                    // 트리거 기반 데이터 사용 (저장된 데이터 조회 시)
                    const employeeInSavedData = employeesFromSavedData.find((emp: any) => emp.name === employee.name);
                    const monthlyValues = employeeInSavedData?.monthlyCosts || new Array(12).fill(0);
                    // 저장된 데이터 사용
                    
                    return {
                        employee,
                        monthlyValues
                    };
                } else {
                    // 실시간 데이터 사용 - 미리 조회한 HR Cost 데이터에서 찾기
                    // 실시간 HR Cost 사용
                    const employeeHRCost = allHRCostData.find((emp: any) => emp.id === employee.id || emp.name === employee.name);
                    
                    if (employeeHRCost?.monthlyData) {
                        // 월별 데이터에서 monthlyLaborCost 추출
                        const monthlyValues = Array.from({length: 12}, (_, i) => {
                            const monthData = employeeHRCost.monthlyData[(i + 1).toString()];
                            return monthData?.monthlyLaborCost || 0;
                        });
                        // API에서 가져온 데이터
                        
                        return {
                            employee,
                            monthlyValues,
                            monthlyLaborCost: monthlyValues[0] || 0
                        };
                    } else {
                        // HR Cost 데이터 없음
                        const monthlyValues = new Array(12).fill(0);
                        return {
                            employee,
                            monthlyValues,
                            monthlyLaborCost: 0
                        };
                    }
                }
            });

            // 결과 처리 - 직원별 행 생성
            employeeHRDataPreCalc.forEach(({employee, monthlyValues}: any) => {
                annualSalaryRows.push({
                    type: 'item',
                    label: employee.name,
                    indent: 1,
                    values: monthlyValues
                });
            });

            // Annual Salary 총계 = 화면에 표시되는 직원 행들의 월별 합계
            // 이미 화면에 표시된 직원 행들의 값을 그대로 사용 (소수점 제거)
            annualSalaryRows.forEach(row => {
                if (row.values) {
                    row.values.forEach((cost: number, monthIndex: number) => {
                        annualSalaryTotals[monthIndex] += Math.round(cost || 0);
                    });
                }
            });

            // OPEX 데이터는 이미 위에서 가져왔으므로 여기서는 값만 추출
            const indirectOpexValues = yearlyOpexData.indirect;
            const directOpexValues = yearlyOpexData.direct;

            // 컨설팅 데이터 처리: savedData가 있으면 트리거 데이터 사용, 없으면 실시간 PPE 조회
            let ppeProjectsData: {[key: string]: number[]} = {};
            let consultingTotals = new Array(12).fill(0);

            if (savedData && savedData.consultingDynamicProjects) {
                // 트리거 기반 데이터 사용 (저장된 데이터 조회 시)
                ppeProjectsData = savedData.consultingDynamicProjects;
            } else {
                // 실시간 PPE 데이터 조회 (편집 시) - 기존 로직 사용
                ppeProjectsData = consultingDynamicProjects;
            }

            // 프로젝트별 데이터 합산하여 컨설팅부분 총계 계산
            Object.values(ppeProjectsData).forEach((monthlyData: number[]) => {
                monthlyData.forEach((val, monthIndex) => {
                    consultingTotals[monthIndex] += val || 0;
                });
            });

            // 컨설팅 프로젝트 데이터 설정
            setConsultingDynamicProjects(ppeProjectsData);

            // 기존 데이터에서 Annual Salary 섹션 업데이트
            // savedData가 null일 때 기본 행 구조 생성 (initialFinancialData.rows는 빈 배열이므로)
            let updatedRows: RowData[];

            if (dynamicFinancialData.rows.length > 0) {
                updatedRows = [...dynamicFinancialData.rows];
            } else {
                // 데이터가 없을 때 기본 행 구조 생성
                updatedRows = [
                    // A. 현금흐름 섹션
                    { type: 'section', label: 'A. 현금흐름', values: new Array(12).fill(0) },
                    { type: 'header', label: '기말현금', values: new Array(12).fill(0) },
                    { type: 'header', label: '기초현금', values: new Array(12).fill(0) },
                    // B. 비용 섹션
                    { type: 'total', label: '지출합계', values: new Array(12).fill(0) },
                    { type: 'total', label: 'Annual Salary', values: annualSalaryTotals },
                    // 직원 rows는 아래에서 삽입됨
                    { type: 'total', label: 'Out sourcing & Restructuring', values: new Array(12).fill(0) },
                    // b-3. 고정비
                    { type: 'section', label: 'b-3. 고정비', values: new Array(12).fill(0) },
                    { type: 'item', label: 'indirect opex', indent: 1, values: indirectOpexValues },
                    { type: 'item', label: 'Summer Bonus & Welfare', indent: 1, values: new Array(12).fill(0) },
                    // b-4. 변동비
                    { type: 'section', label: 'b-4. 변동비', values: new Array(12).fill(0) },
                    { type: 'item', label: 'Project Expense', indent: 1, values: projectExpenseValues },
                    { type: 'item', label: 'direct opex', indent: 1, values: directOpexValues },
                    { type: 'item', label: 'T & E Expense', indent: 1, values: new Array(12).fill(0) },
                    { type: 'spacer' },
                    // C. 매출 섹션
                    { type: 'section', label: '연구부분', values: new Array(12).fill(0) },
                    { type: 'item', label: '01. Research Consulting', indent: 1, values: new Array(12).fill(0) },
                    { type: 'item', label: '02. CRO & Grants', indent: 1, values: new Array(12).fill(0) },
                    { type: 'item', label: '03. BPO (manuscription)', indent: 1, values: new Array(12).fill(0) },
                    { type: 'section', label: '컨설팅부분', values: consultingTotals },
                    { type: 'total', label: '총 매출', values: new Array(12).fill(0) },
                ];
            }

            // Annual Salary 행 찾기
            const annualSalaryIndex = updatedRows.findIndex(row => row.label === 'Annual Salary');
            if (annualSalaryIndex !== -1) {
                // Annual Salary 총계 업데이트
                updatedRows[annualSalaryIndex] = {
                    ...updatedRows[annualSalaryIndex],
                    values: annualSalaryTotals
                };

                // 기존 직원 데이터 제거 (Annual Salary 다음부터 Out sourcing & Restructuring 전까지)
                let removeStartIndex = annualSalaryIndex + 1;
                let removeEndIndex = removeStartIndex;

                while (removeEndIndex < updatedRows.length &&
                    updatedRows[removeEndIndex].label !== 'Out sourcing & Restructuring') {
                    removeEndIndex++;
                }

                // 기존 직원 데이터 제거하고 새 데이터 삽입
                updatedRows.splice(removeStartIndex, removeEndIndex - removeStartIndex, ...annualSalaryRows);
            }

            // 연구부분 추가 항목 총계 계산
            const researchAdditionalTotals = new Array(12).fill(0);
            itemsToUse.forEach(item => {
                item.values.forEach((val, monthIndex) => {
                    researchAdditionalTotals[monthIndex] += val;
                });
            });

            // Project Expense는 이미 위에서 계산됨

            // HR Cost API에서 상여금과 복리후생비 총합을 가져와서 Summer Bonus & Welfare 7월에 설정
            // 단, 이미 편집된 값이 있으면 자동 계산하지 않음
            let summerBonusWelfareValues = currentEditableData['Summer Bonus & Welfare'] || new Array(12).fill(0);
            
            // Summer Bonus & Welfare가 비어있거나 모두 0이면 자동 계산
            const hasExistingValues = summerBonusWelfareValues.some((val: number) => val !== 0);
            
            if (!hasExistingValues) {
                try {
                    const hrCostAllResponse = await apiClient.get(`/employees/hr-cost-all/${selectedYear}`);
                    const allEmployeeHRCost = hrCostAllResponse as any[];
                    
                    // 모든 직원의 상여금과 복리후생비 총합 계산
                    let totalBonusAmount = 0;
                    let totalWelfareCost = 0;
                    
                    allEmployeeHRCost.forEach(employeeData => {
                        const hrCost = employeeData.hrCost;
                        if (hrCost) {
                            totalBonusAmount += hrCost.bonusAmount || 0;
                            totalWelfareCost += hrCost.welfareCost || 0;
                        }
                    });
                    
                    const totalSummerBonusWelfare = totalBonusAmount + totalWelfareCost;
                    
                    // 7월(인덱스 6)에 총합 설정 (원래 값 그대로, formatNumber 함수가 만원 단위로 표시)
                    summerBonusWelfareValues = new Array(12).fill(0);
                    summerBonusWelfareValues[6] = totalSummerBonusWelfare; // 원래 값 그대로 저장
                    
                    // editableData 업데이트 (기존 값 보존)
                    setEditableData(prev => ({
                        ...prev,
                        'Summer Bonus & Welfare': [...summerBonusWelfareValues],
                        // Project Expense는 항상 실시간 계산된 값 사용 
                        'Project Expense': [...projectExpenseValues],
                        'T & E Expense': currentEditableData['T & E Expense'] || prev['T & E Expense'] || new Array(12).fill(0),
                        // 기말현금과 기초현금도 기존 값 유지
                        '기말현금': currentEditableData['기말현금'] || prev['기말현금'] || new Array(12).fill(0),
                        '기초현금': currentEditableData['기초현금'] || prev['기초현금'] || new Array(12).fill(0)
                    }));
                    
                    // researchItems도 기존 값 보존
                    if (Object.keys(currentResearchItems).length > 0) {
                        setResearchItems(prev => ({
                            ...prev,
                            ...currentResearchItems
                        }));
                    }
                    
                } catch (error) {
                    showToast('HR Cost 데이터를 불러올 수 없습니다.', 'error');
                }
            } else {
                // 모든 편집가능한 데이터의 기존 값 보존
                setEditableData(prev => ({
                    ...prev,
                    'Summer Bonus & Welfare': currentEditableData['Summer Bonus & Welfare'] || prev['Summer Bonus & Welfare'] || new Array(12).fill(0),
                    'Project Expense': [...projectExpenseValues], // Project Expense는 항상 실시간 계산된 값 사용
                    'T & E Expense': currentEditableData['T & E Expense'] || prev['T & E Expense'] || new Array(12).fill(0),
                    '기말현금': currentEditableData['기말현금'] || prev['기말현금'] || new Array(12).fill(0),
                    '기초현금': currentEditableData['기초현금'] || prev['기초현금'] || new Array(12).fill(0)
                }));
                
                // researchItems도 기존 값 보존
                if (Object.keys(currentResearchItems).length > 0) {
                    setResearchItems(prev => ({
                        ...prev,
                        ...currentResearchItems
                    }));
                }
            }

            // editableData, OPEX 데이터, 컨설팅 데이터로 행들 업데이트
            const finalUpdatedRows = updatedRows.map(row => {
                if (row.label === 'Summer Bonus & Welfare') {
                    // Summer Bonus & Welfare는 계산된 값 사용
                    return {
                        ...row,
                        values: [...summerBonusWelfareValues]
                    };
                } else if (row.label === 'Project Expense') {
                    // Project Expense는 editableData에서 가져오기 (실시간 계산된 값)
                    return {
                        ...row,
                        values: [...(currentEditableData['Project Expense'] || new Array(12).fill(0))]
                    };
                } else if (row.label === 'T & E Expense') {
                    // T & E Expense는 백업된 데이터에서 가져오기
                    const values = currentEditableData['T & E Expense'] || new Array(12).fill(0);
                    return {
                        ...row,
                        values: [...values]
                    };
                } else if (row.label === 'indirect opex') {
                    // indirect OPEX 데이터
                    return {
                        ...row,
                        values: indirectOpexValues
                    };
                } else if (row.label === 'direct opex') {
                    // direct OPEX 데이터
                    return {
                        ...row,
                        values: directOpexValues
                    };
                } else if (row.label === '연구부분') {
                    // 연구부분 고정 항목들의 총계 + 추가 항목들 총계
                    const researchFixedTotals = new Array(12).fill(0);
                    (Object.values(currentResearchItems) as number[][]).forEach(values => {
                        values.forEach((val, monthIndex) => {
                            researchFixedTotals[monthIndex] += val;
                        });
                    });
                    
                    const combinedResearchTotals = researchFixedTotals.map((val, idx) => 
                        val + researchAdditionalTotals[idx]
                    );
                    
                    return {
                        ...row,
                        values: combinedResearchTotals
                    };
                } else if (row.label === '기말현금') {
                    // 기말현금: savedData 우선, 없으면 currentEditableData 사용
                    const 기말현금Values = savedData?.editableData?.endingCash
                        ? [...savedData.editableData.endingCash]
                        : currentEditableData['기말현금'] || new Array(12).fill(0);

                    return {
                        ...row,
                        values: [...기말현금Values]
                    };
                } else if (row.label === '기초현금') {
                    // 기초현금: savedData 우선, 없으면 currentEditableData 사용
                    const 기초현금Values = savedData?.editableData?.beginningCash
                        ? [...savedData.editableData.beginningCash]
                        : currentEditableData['기초현금'] || new Array(12).fill(0);

                    return {
                        ...row,
                        values: [...기초현금Values]
                    };
                } else if (['01. Research Consulting', '02. CRO & Grants', '03. BPO (manuscription)'].includes(row.label!)) {
                    // 연구부분 개별 항목들
                    const values = currentResearchItems[row.label as keyof typeof currentResearchItems] || new Array(12).fill(0);

                    return {
                        ...row,
                        values: [...values]
                    };
                } else if (row.label === '지출합계') {
                    // 지출합계 = 당월 인건비 합계 + Summer Bonus & Welfare + Project Expense + T & E Expense + indirect opex + direct opex + Out sourcing & Restructuring
                    const 지출합계Values = new Array(12).fill(0);


                    // 현재 렌더링되는 모든 행을 다시 확인
                    const currentRows = [...finalUpdatedRows];

                    // 1. Annual Salary (당월 인건비 합계)
                    const annualSalaryRow = currentRows.find(r => r.label === 'Annual Salary');
                    if (annualSalaryRow?.values) {
                        annualSalaryRow.values.forEach((val, idx) => {
                            const numVal = Number(val) || 0;
                            지출합계Values[idx] += numVal;
                        });
                    }

                    // 2. Summer Bonus & Welfare
                    const summerBonusRow = currentRows.find(r => r.label === 'Summer Bonus & Welfare');
                    if (summerBonusRow?.values) {
                        summerBonusRow.values.forEach((val, idx) => {
                            const numVal = Number(val) || 0;
                            지출합계Values[idx] += numVal;
                        });
                        // Summer Bonus & Welfare 계산 완료
                    }

                    // 3. Project Expense
                    const projectExpenseRow = currentRows.find(r => r.label === 'Project Expense');
                    if (projectExpenseRow?.values) {
                        projectExpenseRow.values.forEach((val, idx) => {
                            const numVal = Number(val) || 0;
                            지출합계Values[idx] += numVal;
                        });
                        // Project Expense 계산 완료
                    }

                    // 4. T & E Expense
                    const teExpenseRow = currentRows.find(r => r.label === 'T & E Expense');
                    if (teExpenseRow?.values) {
                        teExpenseRow.values.forEach((val, idx) => {
                            const numVal = Number(val) || 0;
                            지출합계Values[idx] += numVal;
                        });
                        // T & E Expense 계산 완료
                    }

                    // 5. indirect opex
                    const indirectOpexRow = currentRows.find(r => r.label === 'indirect opex');
                    if (indirectOpexRow?.values) {
                        indirectOpexRow.values.forEach((val, idx) => {
                            const numVal = Number(val) || 0;
                            지출합계Values[idx] += numVal;
                        });
                        // indirect opex 계산 완료
                    }

                    // 6. direct opex
                    const directOpexRow = currentRows.find(r => r.label === 'direct opex');
                    if (directOpexRow?.values) {
                        directOpexRow.values.forEach((val, idx) => {
                            const numVal = Number(val) || 0;
                            지출합계Values[idx] += numVal;
                        });
                        // direct opex 계산 완료
                    }

                    // 7. Out sourcing & Restructuring
                    const outSourcingRow = currentRows.find(r => r.label === 'Out sourcing & Restructuring');
                    if (outSourcingRow?.values) {
                        outSourcingRow.values.forEach((val, idx) => {
                            const numVal = Number(val) || 0;
                            지출합계Values[idx] += numVal;
                        });
                        // Out sourcing & Restructuring 계산 완료
                    } else {
                        // Out sourcing & Restructuring 기본값 사용
                    }


                    return {
                        ...row,
                        values: 지출합계Values
                    };
                } else if (row.label === '총 매출') {
                    // 총 매출 = 연구부분 총계 + 컨설팅부분 총계
                    const researchFixedTotals = new Array(12).fill(0);
                    (Object.values(currentResearchItems) as number[][]).forEach(values => {
                        values.forEach((val, monthIndex) => {
                            researchFixedTotals[monthIndex] += val;
                        });
                    });
                    
                    const combinedResearchTotals = researchFixedTotals.map((val, idx) => 
                        val + researchAdditionalTotals[idx]
                    );
                    
                    const totalRevenueTotals = combinedResearchTotals.map((val, idx) => 
                        val + consultingTotals[idx]
                    );                    

                    return {
                        ...row,
                        values: totalRevenueTotals
                    };
                } else if (row.label === '컨설팅부분') {
                    // 컨설팅부분 총계 (PPE 기반 월별 매출 데이터만 사용)
                    return {
                        ...row,
                        values: consultingTotals
                    };
                }
                // 개별 컨설팅 항목들은 이제 동적으로 추가됨 (하드코딩 제거)
                return row;
            });

            // 연구부분에 동적 항목들 추가
            const researchStartIndex = finalUpdatedRows.findIndex(row => row?.label === '연구부분');
            
            if (researchStartIndex !== -1) {
                // 연구부분의 고정 항목들(01, 02, 03) 다음에 동적 항목들 삽입
                const researchFixedEndIndex = finalUpdatedRows.findIndex((row, idx) => 
                    idx > researchStartIndex && row?.label?.startsWith('03.')
                );
                
                if (researchFixedEndIndex !== -1) {
                    const newResearchRows = itemsToUse.map(item => ({
                        type: 'item' as const,
                        label: item.label,
                        indent: 1,
                        values: [...item.values],
                        sectionId: 'research' // 연구 사업부 섹션 표시
                    }));
                    // 03번 항목 다음에 동적 항목들 삽입
                    finalUpdatedRows.splice(researchFixedEndIndex + 1, 0, ...newResearchRows);
                } else {
                }
            } else {
            }

            // 컨설팅부분에 PPE 프로젝트들 동적 추가
            const consultingStartIndex = finalUpdatedRows.findIndex(row => row?.label === '컨설팅부분');
            if (consultingStartIndex !== -1) {
                // 컨설팅부분 다음에 PPE 프로젝트들 삽입
                const newConsultingRows = Object.entries(ppeProjectsData).map(([projectName, monthlyData]) => ({
                    type: 'item' as const,
                    label: projectName,
                    indent: 1,
                    values: [...monthlyData]
                }));

                // 컨설팅부분 섹션 다음에 동적 프로젝트들 삽입
                finalUpdatedRows.splice(consultingStartIndex + 1, 0, ...newConsultingRows);

                // 추가된 프로젝트들의 월별 합계를 다시 계산하여 컨설팅부분 섹션에 적용
                const recalculatedConsultingTotals = new Array(12).fill(0);
                newConsultingRows.forEach(row => {
                    row.values?.forEach((val, monthIndex) => {
                        recalculatedConsultingTotals[monthIndex] += val || 0;
                    });
                });

                // 컨설팅부분 섹션의 값 업데이트
                if (finalUpdatedRows[consultingStartIndex]) {
                    finalUpdatedRows[consultingStartIndex] = {
                        ...finalUpdatedRows[consultingStartIndex],
                        values: recalculatedConsultingTotals
                    };
                }
            }

            // b-2. 외주인건비에 CF 직원들 동적 추가 (savedData가 null일 때 state에서 가져옴)
            const outSourcingIndex = finalUpdatedRows.findIndex(row => row?.label === 'Out sourcing & Restructuring');
            if (outSourcingIndex !== -1 && cfAdditionalEmployees.length > 0) {
                // CF 직원 행들 생성 (sectionId 포함)
                const newCfEmployeeRows = cfAdditionalEmployees.map(emp => ({
                    type: 'item' as const,
                    label: emp.name,
                    indent: 1,
                    values: [...emp.monthlyCosts],
                    sectionId: 'b-2' // 외주인건비 섹션 표시
                }));

                // Out sourcing & Restructuring 다음에 CF 직원들 삽입
                finalUpdatedRows.splice(outSourcingIndex + 1, 0, ...newCfEmployeeRows);

                // CF 직원들의 월별 합계를 다시 계산하여 Out sourcing & Restructuring 섹션에 적용
                const recalculatedOutSourcingTotals = new Array(12).fill(0);
                newCfEmployeeRows.forEach(row => {
                    row.values?.forEach((val, monthIndex) => {
                        recalculatedOutSourcingTotals[monthIndex] += val || 0;
                    });
                });

                // Out sourcing & Restructuring 섹션의 값 업데이트
                if (finalUpdatedRows[outSourcingIndex]) {
                    finalUpdatedRows[outSourcingIndex] = {
                        ...finalUpdatedRows[outSourcingIndex],
                        values: recalculatedOutSourcingTotals
                    };
                }
            }

            // 지출합계를 항상 최신값으로 재계산
            const rowsWithRecalculatedExpenseTotal = finalUpdatedRows.map(row => {
                if (row.label === '지출합계') {
                    // 지출합계 재계산
                    const 지출합계Values = new Array(12).fill(0);

                    // finalUpdatedRows에서 각 항목 찾기
                    const annualSalaryRow = finalUpdatedRows.find(r => r.label === 'Annual Salary');
                    const summerBonusRow = finalUpdatedRows.find(r => r.label === 'Summer Bonus & Welfare');
                    const projectExpenseRow = finalUpdatedRows.find(r => r.label === 'Project Expense');
                    const teExpenseRow = finalUpdatedRows.find(r => r.label === 'T & E Expense');
                    const indirectOpexRow = finalUpdatedRows.find(r => r.label === 'indirect opex');
                    const directOpexRow = finalUpdatedRows.find(r => r.label === 'direct opex');
                    const outSourcingRow = finalUpdatedRows.find(r => r.label === 'Out sourcing & Restructuring');

                    // 각 항목 더하기
                    for (let idx = 0; idx < 12; idx++) {
                        지출합계Values[idx] =
                            (Number(annualSalaryRow?.values?.[idx]) || 0) +
                            (Number(summerBonusRow?.values?.[idx]) || 0) +
                            (Number(projectExpenseRow?.values?.[idx]) || 0) +
                            (Number(teExpenseRow?.values?.[idx]) || 0) +
                            (Number(indirectOpexRow?.values?.[idx]) || 0) +
                            (Number(directOpexRow?.values?.[idx]) || 0) +
                            (Number(outSourcingRow?.values?.[idx]) || 0);
                    }

                    // 지출합계 재계산 완료

                    return {
                        ...row,
                        values: 지출합계Values
                    };
                }
                return row;
            });

            // 📊 DB의 calculated_totals와 현재 지출합계 비교하여 자동 재계산 판단
            const shouldRecalculate = await (async () => {
                try {
                    const savedData = await cashFlowManagementApi.getByYear(selectedYear);

                    if (savedData && savedData.length > 0) {
                        const dbExpenseTotals = savedData[0].calculatedTotals?.monthlyTotals || [];
                        const currentExpenseRow = rowsWithRecalculatedExpenseTotal.find(row => row.label === '지출합계');
                        const currentExpenseTotals = currentExpenseRow?.values || [];

                        // 합계가 다르면 재계산 필요
                        const differences = dbExpenseTotals.map((dbTotal: number, index: number) => {
                            const currentTotal = currentExpenseTotals[index] || 0;
                            const diff = Math.abs((dbTotal || 0) - currentTotal);
                            return { month: index + 1, db: dbTotal || 0, current: currentTotal, diff };
                        });

                        const isDifferent = differences.some((d: any) => d.diff > 0.01);

                        return isDifferent;
                    }
                    return false;
                } catch (error) {
                    return false;
                }
            })();

            // 지출합계 재계산 후 기말현금도 자동 재계산
            // customResearchItems가 전달되면 연구부분 추가/삭제이므로 항상 재계산
            const needsRecalculation = shouldRecalculate || !!customResearchItems;
            const finalRowsWithEndingCash = recalculateEndingCash(rowsWithRecalculatedExpenseTotal, needsRecalculation);

            setDynamicFinancialData({
                ...initialFinancialData,
                rows: finalRowsWithEndingCash.filter(row => row !== undefined)
            });

            // 재계산된 기말현금, 기초현금 값을 editableData에 반영 (확정 버튼으로 저장)
            if (needsRecalculation) {
                setTimeout(() => {
                    const 기말현금Row = finalRowsWithEndingCash.find(row => row.label === '기말현금');
                    const 기초현금Row = finalRowsWithEndingCash.find(row => row.label === '기초현금');

                    setEditableData(prev => ({
                        ...prev,
                        '기말현금': 기말현금Row?.values || prev['기말현금'],
                        '기초현금': 기초현금Row?.values || prev['기초현금']
                    }));
                }, 200);
            }

            // 화면 렌더링 후 Annual Salary 합계 재계산
            setTimeout(() => {
                updateAnnualSalaryFromDisplayedData();
            }, 100);

        } catch (error) {
            showToast('데이터 생성 중 오류가 발생했습니다.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // 데이터 저장 함수
    const saveCashFlowData = async (customEditableData?: any, customResearchItems?: any, customResearchAdditionalItems?: any, customCfAdditionalEmployees?: any) => {
        // 파라미터로 전달받은 데이터가 있으면 사용, 없으면 기본 상태 사용
        const dataToSave = customEditableData || editableData;
        const researchDataToSave = customResearchItems || researchItems;
        const researchAdditionalDataToSave = customResearchAdditionalItems || researchAdditionalItems;
        const cfEmployeesToSave = customCfAdditionalEmployees || cfAdditionalEmployees;
        try {
            // 직원 데이터는 트리거로 자동 계산되므로 저장하지 않음 (제거)

            // OPEX 데이터는 트리거로 자동 계산되므로 저장하지 않음

            // 총계 계산은 백엔드에서 처리되므로 제거

            // 현재 로드된 데이터에서 트리거 기반 데이터 가져오기
            const getCurrentTriggerData = async () => {
                try {
                    const yearData = await cashFlowManagementApi.getByYear(selectedYear);
                    return yearData && yearData.length > 0 ? yearData[0] : null;
                } catch (error) {
                    return null;
                }
            };

            const currentData = await getCurrentTriggerData();

            // 화면에 표시된 값을 그대로 DB에 저장
            // dynamicFinancialData에서 현재 화면의 기말현금/기초현금 값 가져오기
            const 기말현금Row = dynamicFinancialData.rows.find(row => row.label === '기말현금');
            const 기초현금Row = dynamicFinancialData.rows.find(row => row.label === '기초현금');

            // 편집 가능한 항목들 + 트리거 기반 데이터 저장

            const cashFlowData: Partial<CashFlowManagementData> = {
                year: selectedYear,
                // 편집 가능한 전용 컬럼들 (숫자 배열로 정리)
                summerBonusWelfare: sanitizeNumberArray(dataToSave['Summer Bonus & Welfare'] || new Array(12).fill(0)),
                projectExpense: sanitizeNumberArray(dataToSave['Project Expense'] || new Array(12).fill(0)),
                tAndEExpense: sanitizeNumberArray(dataToSave['T & E Expense'] || new Array(12).fill(0)),
                // outSourcingRestructuring는 CF 직원 합계로 계산
                outSourcingRestructuring: (() => {
                    const outSourcingTotals = new Array(12).fill(0);
                    cfEmployeesToSave.forEach((emp: any) => {
                        emp.monthlyCosts?.forEach((cost: number, idx: number) => {
                            outSourcingTotals[idx] += Math.round(cost || 0);
                        });
                    });
                    return outSourcingTotals;
                })(),
                // 기말현금/기초현금 - 화면(dynamicFinancialData)의 값을 우선 사용
                editableData: {
                    endingCash: (() => {
                        // 화면에 표시된 값(dynamicFinancialData)을 우선 사용
                        const calculatedValues = 기말현금Row?.values || [];
                        const editableValues = dataToSave['기말현금'] || [];
                        return sanitizeNumberArray(
                            calculatedValues.length > 0 ? calculatedValues :
                            editableValues.length > 0 ? editableValues :
                            new Array(12).fill(0)
                        );
                    })(),
                    beginningCash: (() => {
                        // 화면에 표시된 값(dynamicFinancialData)을 우선 사용
                        const calculatedValues = 기초현금Row?.values || [];
                        const editableValues = dataToSave['기초현금'] || [];
                        return sanitizeNumberArray(
                            calculatedValues.length > 0 ? calculatedValues :
                            editableValues.length > 0 ? editableValues :
                            new Array(12).fill(0)
                        );
                    })()
                },
                // 연구부분 (숫자 배열로 정리)
                researchItems: {
                    fixedItems: {
                        researchConsulting: sanitizeNumberArray(researchDataToSave['01. Research Consulting'] || new Array(12).fill(0)),
                        croGrants: sanitizeNumberArray(researchDataToSave['02. CRO & Grants'] || new Array(12).fill(0)),
                        bpoManuscription: sanitizeNumberArray(researchDataToSave['03. BPO (manuscription)'] || new Array(12).fill(0))
                    },
                    displayLabels: researchDisplayLabels,
                    additionalItems: researchAdditionalDataToSave.map((item: any) => ({
                        label: item.label,
                        values: sanitizeNumberArray(item.values)
                    }))
                },
                // CF 전용 추가 직원들 (전체 저장 방식)
                cfAdditionalEmployees: cfEmployeesToSave.map((emp: any) => ({
                    id: emp.id,
                    name: emp.name,
                    monthlyCosts: sanitizeNumberArray(emp.monthlyCosts)
                })),
                // 트리거로 계산된 데이터들도 보존하여 저장 (초기화 방지)
                annualSalaryData: currentData?.annualSalaryData || undefined,
                consultingDynamicProjects: currentData?.consultingDynamicProjects || undefined,
                opexData: currentData?.opexData || undefined,
                // calculatedTotals는 현재 화면의 실시간 계산 값을 저장
                calculatedTotals: {
                    monthlyTotals: (() => {
                        // 현재 dynamicFinancialData에서 지출합계 행 찾기
                        const expenseRow = dynamicFinancialData.rows.find(row => row.label === '지출합계');
                        return expenseRow?.values ? sanitizeNumberArray(expenseRow.values) : new Array(12).fill(0);
                    })(),
                    cumulativeTotals: currentData?.calculatedTotals?.cumulativeTotals || new Array(12).fill(0)
                }
            };

            // year별로 저장
            await cashFlowManagementApi.updateByYear(selectedYear, cashFlowData);

            // CF 전용 직원들도 전체 저장 방식으로 포함됨 (cfAdditionalEmployees)

            // 📝 모든 연도에서 1월 기초현금 수동 입력 모드 (다음 연도 자동 업데이트 제거)

            // Project Expense는 이미 cashFlowData에 포함되어 저장되므로 별도 API 호출 불필요
        } catch (error: any) {
            throw error; // 에러를 다시 throw하여 호출한 곳에서 처리하도록 함
        }
    };

    // 기말현금 재계산 함수 (통합)
    // includeFirstMonth: true면 1월부터 전체 재계산, false면 2월부터만 재계산
    const recalculateEndingCash = (updatedRows: any[], includeFirstMonth: boolean = true) => {
        const 기말현금Row = updatedRows.find(row => row.label === '기말현금');
        const 기초현금Row = updatedRows.find(row => row.label === '기초현금');
        const 지출합계Row = updatedRows.find(row => row.label === '지출합계');
        const 총매출Row = updatedRows.find(row => row.label === '총 매출');

        if (기말현금Row && 기초현금Row && 지출합계Row && 총매출Row) {
            const 기말현금Values = [...(기말현금Row.values || new Array(12).fill(0))];
            const 기초현금Values = [...(기초현금Row.values || new Array(12).fill(0))];

            // 1월: includeFirstMonth가 true이면 계산, 아니면 기존값 유지
            if (includeFirstMonth) {
                const 지출합계_1월 = 지출합계Row.values?.[0] || 0;
                const 총매출_1월 = 총매출Row.values?.[0] || 0;
                기말현금Values[0] = 기초현금Values[0] + 총매출_1월 - 지출합계_1월;
            }

            // 2월~12월: 순차적 계산
            for (let i = 1; i < 12; i++) {
                기초현금Values[i] = 기말현금Values[i - 1] || 0;
                const 지출합계 = 지출합계Row.values?.[i] || 0;
                const 총매출 = 총매출Row.values?.[i] || 0;
                기말현금Values[i] = 기초현금Values[i] + 총매출 - 지출합계;
            }

            return updatedRows.map(row => {
                if (row.label === '기말현금') {
                    return { ...row, values: 기말현금Values };
                } else if (row.label === '기초현금') {
                    return { ...row, values: 기초현금Values };
                }
                return row;
            });
        }

        return updatedRows;
    };




    // 화면에 표시된 직원 행들의 값을 사용하여 Annual Salary 합계 업데이트
    const updateAnnualSalaryFromDisplayedData = () => {
        // updateAnnualSalaryFromDisplayedData 실행
        setDynamicFinancialData(prev => {
            const updatedRows = [...prev.rows];
            
            // 직원 행들 찾기 (type: 'item'이고 Annual Salary 다음부터 Summer Bonus & Welfare 전까지)
            const annualSalaryIndex = updatedRows.findIndex(row => row.label === 'Annual Salary');
            if (annualSalaryIndex === -1) {
                return prev;
            }
            
            let employeeStartIndex = annualSalaryIndex + 1;
            let employeeEndIndex = employeeStartIndex;
            
            // Summer Bonus & Welfare까지 찾기
            while (employeeEndIndex < updatedRows.length && 
                   updatedRows[employeeEndIndex].label !== 'Summer Bonus & Welfare') {
                employeeEndIndex++;
            }
            // 직원 행들에서 월별 합계 계산
            const newAnnualSalaryTotals = new Array(12).fill(0);
            for (let i = employeeStartIndex; i < employeeEndIndex; i++) {
                const row = updatedRows[i];
                if (row.type === 'item' && row.values) {
                    row.values.forEach((cost, monthIndex) => {
                        newAnnualSalaryTotals[monthIndex] += Math.round(cost || 0);
                    });
                }
            }

            // 새로 계산된 Annual Salary 총계

            // Annual Salary 총계 업데이트
            updatedRows[annualSalaryIndex] = {
                ...updatedRows[annualSalaryIndex],
                values: newAnnualSalaryTotals
            };
            
            return { ...prev, rows: updatedRows };
        });
    };


    // 데이터 로드 함수 - 조회 시 직원 변경 감지 및 자동계산
    const loadCashFlowData = async (year: number = selectedYear) => {
        setLoading(true);
        try {
            // year별로 조회
            const yearData = await cashFlowManagementApi.getByYear(year);

            if (!yearData || yearData.length === 0) {
                // 데이터가 없어도 Project Expense 등 실시간 계산 필요
                await generateDynamicData([], {}, {}, null);
                setLoading(false);
                return;
            }

            const savedData = yearData[0];

            // 📝 모든 연도에서 1월 기초현금을 직접 입력 가능 (이전 연도 자동 연결 제거)
            const savedBeginningCash = savedData.editableData?.beginningCash || new Array(12).fill(0);
            const beginningCashValues = [...savedBeginningCash];

            // 새로운 전용 컬럼들과 기존 editableData에서 데이터 로드
            // Project Expense는 실시간 계산이므로 DB값 사용하지 않음

            setEditableData({
                'Summer Bonus & Welfare': sanitizeNumberArray(savedData.summerBonusWelfare),
                'Project Expense': new Array(12).fill(0), // 실시간 계산값으로 대체될 예정
                'T & E Expense': sanitizeNumberArray(savedData.tAndEExpense),
                '기말현금': sanitizeNumberArray(savedData.editableData?.endingCash),
                '기초현금': sanitizeNumberArray(beginningCashValues)
            });
            
            // CF 전용 추가 직원들 로드
            // 저장 시 일반 직원과 중복 체크를 하므로 로드 시 필터링 불필요
            const cfEmployees = (savedData.cfAdditionalEmployees || [])
                .map((emp: any) => ({
                    id: emp.id,
                    name: emp.name,
                    monthlyCosts: emp.monthlyCosts
                }));
            setCfAdditionalEmployees(cfEmployees);
            
            // 연구부분 항목 복원
            if (savedData.researchItems?.fixedItems) {
                const fixedItems = savedData.researchItems.fixedItems;
                setResearchItems({
                    '01. Research Consulting': fixedItems.researchConsulting || new Array(12).fill(0),
                    '02. CRO & Grants': fixedItems.croGrants || new Array(12).fill(0),
                    '03. BPO (manuscription)': fixedItems.bpoManuscription || new Array(12).fill(0)
                });
            }

            // 연구부분 표시명 복원
            if (savedData.researchItems?.displayLabels) {
                const displayLabels = savedData.researchItems.displayLabels;
                setResearchDisplayLabels({
                    researchConsulting: displayLabels.researchConsulting || '연구매출',
                    croGrants: displayLabels.croGrants || '투자매출',
                    bpoManuscription: displayLabels.bpoManuscription || 'BPO'
                });
            }

            if (savedData.researchItems?.additionalItems) {
                setResearchAdditionalItems(savedData.researchItems.additionalItems);
            }
            
            // 트리거로 계산된 컨설팅 프로젝트 사용
            if (savedData.consultingDynamicProjects) {
                setConsultingDynamicProjects(savedData.consultingDynamicProjects);
            }

            await generateDynamicData(savedData.researchItems?.additionalItems || [], savedData.editableData, savedData.researchItems, savedData);

            // 로딩 완료
            setLoading(false);
            
        } catch (error: any) {
            setLoading(false);
        }
    };


    // 연도 목록 로드
    useEffect(() => {
        const loadAvailableYears = async () => {
            try {
                const years = await cashFlowManagementApi.getAllYears();
                if (years && years.length > 0) {
                    setAvailableYears(years.sort((a, b) => b - a)); // 내림차순 정렬
                }
            } catch (error) {
                showToast('연도 목록을 불러올 수 없습니다.', 'error');
            }
        };
        loadAvailableYears();
    }, []);

    useEffect(() => {
        // 연도가 변경될 때마다 sessionStorage에 저장
        sessionStorage.setItem('cashFlowSelectedYear', selectedYear.toString());
        loadCashFlowData();
    }, [selectedYear]);  // month는 제거 - year별로만 데이터 관리

    // 🎯 로컬스토리지 기반 직원 변경 감지 및 자동 재계산
    useEffect(() => {
        // 초기 직원 데이터를 로컬스토리지에 저장
        const initializeEmployeeData = async () => {
            try {
                const currentEmployees = await employeeApi.getAllVisible(userRole);
                const currentHash = currentEmployees.map(emp => `${emp.id}-${emp.name}`).sort().join(',');
                localStorage.setItem('cashflow_employee_hash', currentHash);
                localStorage.setItem('cashflow_employee_count', currentEmployees.length.toString());
                // 초기 직원 데이터 로컬스토리지 저장
            } catch (error) {
                showToast('초기 직원 데이터 저장에 실패했습니다.', 'error');
            }
        };

        // 페이지 로드 후 5초 뒤에 초기화
        const initTimeout = setTimeout(initializeEmployeeData, 5000);

        return () => clearTimeout(initTimeout);
    }, [selectedYear, userRole]);

    // 🔍 로컬스토리지 변경 감지 (storage 이벤트 리스너)
    useEffect(() => {
        const handleEmployeeChange = async () => {
            try {
                // 직원 변경 감지됨

                // 1. generateDynamicData 호출로 테이블 업데이트 (Annual Salary, Project Expense 등 재계산)
                // generateDynamicData 호출 시작
                await generateDynamicData(researchAdditionalItems, editableData, researchItems, null);
                // generateDynamicData 완료

                // 2. 현재 테이블 데이터로 기말현금 재계산 (엔터키와 동일한 로직)
                // 기말현금 재계산 시작
                setDynamicFinancialData(prev => {
                    const updatedRows = recalculateEndingCash(prev.rows);
                    // 기말현금 재계산 완료
                    return { ...prev, rows: updatedRows };
                });

                // 3. editableData 업데이트 (기초현금, 기말현금)
                setTimeout(() => {
                    setEditableData(prev => {
                        const 기말현금Row = dynamicFinancialData.rows.find(row => row.label === '기말현금');
                        const 기초현금Row = dynamicFinancialData.rows.find(row => row.label === '기초현금');

                        return {
                            ...prev,
                            '기말현금': 기말현금Row?.values || prev['기말현금'],
                            '기초현금': 기초현금Row?.values || prev['기초현금']
                        };
                    });
                }, 100);

                // 4. DB 저장
                setTimeout(async () => {
                    try {
                        await saveCashFlowData(editableData, researchItems, researchAdditionalItems);
                        // 직원 변경에 따른 자동 재계산 및 저장 완료

                        // 저장 후 새로고침
                        setTimeout(() => {
                            reloadWithYear();
                        }, 500);
                    } catch (error) {
                        showToast('자동 재계산 저장에 실패했습니다.', 'error');
                    }
                }, 200);

            } catch (error) {
                showToast('직원 변경 처리 중 오류가 발생했습니다.', 'error');
            }
        };

        // storage 이벤트 리스너 (함수를 변수에 저장하여 정확히 제거)
        const storageHandler = (e: StorageEvent) => {
            if (e.key === 'employee_change_trigger') {
                handleEmployeeChange();
            }
        };

        // Custom 이벤트 리스너 (같은 탭에서의 변경사항)
        const customHandler = () => {
            handleEmployeeChange();
        };

        window.addEventListener('storage', storageHandler);
        window.addEventListener('employeeChanged', customHandler);

        return () => {
            window.removeEventListener('storage', storageHandler);
            window.removeEventListener('employeeChanged', customHandler);
        };
    }, [editableData, researchItems, researchAdditionalItems]);





    // 컨설팅 항목이 변경될 때는 전체 재생성하지 않음 (성능 최적화)


    // 편집 가능한 셀 클릭 핸들러
    const handleEditableClick = (rowLabel: string, monthIndex: number, currentValue: number, sectionId: string | null) => {
        setIsEditing({ rowLabel, monthIndex, sectionId });
        setEditValue(currentValue.toString()); // 이미 만원 단위로 변환된 값
    };

    // 편집 값 저장
    const handleEditSave = () => {
        if (isEditing) {
            const newValue = parseFloat(editValue) || 0;
            
            // Summer Bonus & Welfare, T & E Expense 편집 시 로컬 상태만 업데이트 (확정 버튼으로 저장)
            if (['Summer Bonus & Welfare', 'T & E Expense'].includes(isEditing.rowLabel)) {
                setEditableData(currentData => {
                    const updatedData = {
                        ...currentData,
                        [isEditing.rowLabel]: (currentData[isEditing.rowLabel] || new Array(12).fill(0)).map((val, idx) =>
                            idx === isEditing.monthIndex ? newValue * 10000 : val
                        )
                    };

                    // 지출합계 재계산 (화면 반영용)
                    setTimeout(async () => {
                        await generateDynamicData(researchAdditionalItems, updatedData, researchItems, null);
                    }, 100);

                    return updatedData;
                });
            }
            // 기초현금 1월 편집 시 화면만 업데이트 (확정 버튼으로 저장)
            else if (isEditing.rowLabel === '기초현금' && isEditing.monthIndex === 0) {
                // 기초현금 업데이트
                const updated기초현금 = editableData['기초현금'].map((val, idx) =>
                    idx === 0 ? newValue * 10000 : val // 1월만 수정
                );

                // 현재 화면의 데이터로 기말현금 재계산
                const 지출합계Row = dynamicFinancialData.rows.find(row => row.label === '지출합계');
                const 총매출Row = dynamicFinancialData.rows.find(row => row.label === '총 매출');

                const 기말현금Values = new Array(12).fill(0);
                const 기초현금Values = [...updated기초현금];

                // 1월 기말현금 계산
                기말현금Values[0] = 기초현금Values[0] + (총매출Row?.values?.[0] || 0) - (지출합계Row?.values?.[0] || 0);

                // 2-12월 계산
                for (let i = 1; i < 12; i++) {
                    기초현금Values[i] = 기말현금Values[i - 1];
                    기말현금Values[i] = 기초현금Values[i] + (총매출Row?.values?.[i] || 0) - (지출합계Row?.values?.[i] || 0);
                }

                const updatedEditableData = {
                    ...editableData,
                    '기초현금': 기초현금Values,
                    '기말현금': 기말현금Values
                };

                setEditableData(updatedEditableData);

                // 화면 직접 업데이트 (generateDynamicData 대신 - savedData=null 문제 방지)
                setDynamicFinancialData(prev => {
                    const updatedRows = prev.rows.map(row => {
                        if (row.label === '기초현금') {
                            return { ...row, values: 기초현금Values };
                        } else if (row.label === '기말현금') {
                            return { ...row, values: 기말현금Values };
                        } else if (row.label === 'A. 현금흐름') {
                            // A. 현금흐름 = 기말현금 - 기초현금
                            const cashFlowValues = 기말현금Values.map((val, idx) => val - 기초현금Values[idx]);
                            return { ...row, values: cashFlowValues };
                        }
                        return row;
                    });
                    return { ...prev, rows: updatedRows };
                });

                // 편집 상태 종료 (2236번 블록 건너뛰기)
                setIsEditing(null);
                setEditValue('');
                return;
            }
            // 편집 가능한 고정 데이터 업데이트 (Summer Bonus & Welfare, T & E Expense, 기초현금 제외 - 위에서 처리)
            else if (editableData[isEditing.rowLabel]) {
                // 업데이트된 데이터 미리 계산
                const updatedEditableData = {
                    ...editableData,
                    [isEditing.rowLabel]: editableData[isEditing.rowLabel].map((val, idx) =>
                        idx === isEditing.monthIndex ? newValue * 10000 : val // 만원 단위로 저장
                    )
                };

                setEditableData(updatedEditableData);

                // 다른 편집 시에도 일반 재계산 로직 사용
            }
            
            // 연구부분 고정 항목 업데이트 - 화면만 업데이트 (확정 버튼으로 저장)
            if (researchItems[isEditing.rowLabel as keyof typeof researchItems]) {
                // 업데이트된 연구부분 데이터 준비
                const updatedResearchItems = {
                    ...researchItems,
                    [isEditing.rowLabel]: researchItems[isEditing.rowLabel as keyof typeof researchItems].map((val: number, idx: number) =>
                        idx === isEditing.monthIndex ? newValue * 10000 : val
                    )
                };

                setResearchItems(updatedResearchItems);

                // 연구부분 편집 후 전체 재계산 트리거 (화면 업데이트만, 저장은 확정 버튼으로)
                setTimeout(async () => {
                    await generateDynamicData(researchAdditionalItems, editableData, updatedResearchItems, null);

                    // 연구부분 변경 시 기말현금도 editableData에 반영 (총매출 변경 → 기말현금 변경)
                    setTimeout(() => {
                        setEditableData(prev => {
                            const 기말현금Row = dynamicFinancialData.rows.find(row => row.label === '기말현금');
                            const 기초현금Row = dynamicFinancialData.rows.find(row => row.label === '기초현금');
                            return {
                                ...prev,
                                '기말현금': 기말현금Row?.values || prev['기말현금'],
                                '기초현금': 기초현금Row?.values || prev['기초현금']
                            };
                        });
                    }, 100);
                }, 50);
            }
            
            // 컨설팅부분 고정 항목들은 PPE 기반으로 자동 계산되므로 편집 불가
            // (기존 컨설팅부분 고정 항목 업데이트 로직 제거됨)
            
            // CF 전용 직원 데이터 업데이트 - 화면만 업데이트 (확정 버튼으로 저장)
            // sectionId가 'b-2' (외주인건비)인 경우에만 CF 직원으로 처리
            const cfEmployee = isEditing.sectionId === 'b-2'
                ? cfAdditionalEmployees.find(emp => emp.name === isEditing.rowLabel)
                : null;
            if (cfEmployee) {
                const newMonthlyCosts = [...cfEmployee.monthlyCosts];
                newMonthlyCosts[isEditing.monthIndex] = newValue * 10000; // 만원 단위로 저장

                // 로컬 상태 업데이트 (화면 표시용, 저장은 확정 버튼으로)
                setCfAdditionalEmployees(prev => prev.map(emp => {
                    if (emp.name === isEditing.rowLabel) {
                        return { ...emp, monthlyCosts: newMonthlyCosts };
                    }
                    return emp;
                }));

                // 화면 직접 업데이트 (generateDynamicData 대신)
                setDynamicFinancialData(prev => {
                    // 1. 해당 CF 직원 행 값 업데이트 (b-2 섹션의 해당 직원만)
                    let updatedRows = prev.rows.map(row => {
                        const rowInfo = getRowInfo(row);
                        if (row.label === isEditing.rowLabel && rowInfo.parentId === 'b-2') {
                            const newValues = [...(row.values || new Array(12).fill(0))];
                            newValues[isEditing.monthIndex] = newValue * 10000;
                            return { ...row, values: newValues };
                        }
                        return row;
                    });

                    // 2. Out sourcing & Restructuring 합계 재계산 (CF 직원들 합계)
                    // (b-1. 인건비 Annual Salary는 CF 직원과 무관하므로 재계산하지 않음)
                    updatedRows = updatedRows.map(row => {
                        if (row.label === 'Out sourcing & Restructuring') {
                            const outSourcingTotals = new Array(12).fill(0);
                            // 현재 편집된 CF 직원 상태 반영
                            const updatedCfEmployees = cfAdditionalEmployees.map(emp => {
                                if (emp.name === isEditing.rowLabel) {
                                    const newCosts = [...emp.monthlyCosts];
                                    newCosts[isEditing.monthIndex] = newValue * 10000;
                                    return { ...emp, monthlyCosts: newCosts };
                                }
                                return emp;
                            });
                            updatedCfEmployees.forEach(emp => {
                                emp.monthlyCosts?.forEach((cost, idx) => {
                                    outSourcingTotals[idx] += Math.round(cost || 0);
                                });
                            });
                            return { ...row, values: outSourcingTotals };
                        }
                        return row;
                    });

                    // 3. 지출합계 재계산
                    updatedRows = updatedRows.map(row => {
                        if (row.label === '지출합계') {
                            const 지출합계Values = new Array(12).fill(0);
                            const annualSalaryRow = updatedRows.find(r => r.label === 'Annual Salary');
                            const summerBonusRow = updatedRows.find(r => r.label === 'Summer Bonus & Welfare');
                            const projectExpenseRow = updatedRows.find(r => r.label === 'Project Expense');
                            const teExpenseRow = updatedRows.find(r => r.label === 'T & E Expense');
                            const indirectOpexRow = updatedRows.find(r => r.label === 'indirect opex');
                            const directOpexRow = updatedRows.find(r => r.label === 'direct opex');
                            const outSourcingRow = updatedRows.find(r => r.label === 'Out sourcing & Restructuring');

                            for (let idx = 0; idx < 12; idx++) {
                                지출합계Values[idx] =
                                    (Number(annualSalaryRow?.values?.[idx]) || 0) +
                                    (Number(summerBonusRow?.values?.[idx]) || 0) +
                                    (Number(projectExpenseRow?.values?.[idx]) || 0) +
                                    (Number(teExpenseRow?.values?.[idx]) || 0) +
                                    (Number(indirectOpexRow?.values?.[idx]) || 0) +
                                    (Number(directOpexRow?.values?.[idx]) || 0) +
                                    (Number(outSourcingRow?.values?.[idx]) || 0);
                            }
                            return { ...row, values: 지출합계Values };
                        }
                        return row;
                    });

                    // 4. 기말현금 재계산
                    const finalRows = recalculateEndingCash(updatedRows);

                    // 5. editableData에 기말현금 반영
                    const 기말현금Row = finalRows.find(row => row.label === '기말현금');
                    const 기초현금Row = finalRows.find(row => row.label === '기초현금');
                    if (기말현금Row && 기초현금Row) {
                        setEditableData(prevEdit => ({
                            ...prevEdit,
                            '기말현금': 기말현금Row.values || new Array(12).fill(0),
                            '기초현금': 기초현금Row.values || new Array(12).fill(0)
                        }));
                    }

                    return { ...prev, rows: finalRows };
                });
            }
            
            // 동적 연구 추가 항목 업데이트 - 화면만 업데이트 (확정 버튼으로 저장)
            // sectionId가 'research' (연구 사업부)인 경우에만 연구 추가 항목으로 처리
            const researchAdditionalItem = isEditing.sectionId === 'research'
                ? researchAdditionalItems.find(item => item.label === isEditing.rowLabel)
                : null;
            if (researchAdditionalItem) {
                // 업데이트된 동적 항목 데이터 준비
                const updatedResearchAdditionalItems = researchAdditionalItems.map(item => {
                    if (item.label === isEditing.rowLabel) {
                        const newValues = [...item.values];
                        newValues[isEditing.monthIndex] = newValue * 10000;
                        return { ...item, values: newValues };
                    }
                    return item;
                });

                setResearchAdditionalItems(updatedResearchAdditionalItems);

                // 연구부분 동적 항목 편집 후 전체 재계산 트리거 (화면 업데이트만, 저장은 확정 버튼으로)
                setTimeout(async () => {
                    await generateDynamicData(updatedResearchAdditionalItems, editableData, researchItems, null);

                    // 연구부분 변경 시 기말현금도 editableData에 반영 (총매출 변경 → 기말현금 변경)
                    setTimeout(() => {
                        setEditableData(prev => {
                            const 기말현금Row = dynamicFinancialData.rows.find(row => row.label === '기말현금');
                            const 기초현금Row = dynamicFinancialData.rows.find(row => row.label === '기초현금');
                            return {
                                ...prev,
                                '기말현금': 기말현금Row?.values || prev['기말현금'],
                                '기초현금': 기초현금Row?.values || prev['기초현금']
                            };
                        });
                    }, 100);
                }, 50);
            }
            
            // dynamicFinancialData 업데이트 - 모든 편집에 대해 계산식 트리거
            // 연구부분 항목 여부 확인 (sectionId 기반)
            const isResearchItem = isEditing.sectionId === 'research' && (
                researchItems[isEditing.rowLabel as keyof typeof researchItems] ||
                researchAdditionalItems.some(item => item.label === isEditing.rowLabel)
            );

            setDynamicFinancialData(prev => {
                let updatedRows = prev.rows.map(row => {
                    // 같은 라벨 + 같은 섹션의 행만 업데이트
                    const rowInfo = getRowInfo(row);
                    // sectionId가 null인 경우 (기초현금 등) parentId 체크 생략
                    const sectionMatch = isEditing.sectionId === null
                        ? true
                        : rowInfo.parentId === isEditing.sectionId;
                    if (row.label === isEditing.rowLabel && sectionMatch) {
                        const newValues = [...(row.values || [])];
                        newValues[isEditing.monthIndex] = newValue * 10000;
                        return { ...row, values: newValues };
                    }
                    return row;
                });

                // 연구부분 수정 시 - 연구부분 총계, 총매출, 기말현금 모두 재계산
                if (isResearchItem) {
                    // 1. 연구부분 총계 재계산
                    const researchTotalValues = new Array(12).fill(0);
                    ['01. Research Consulting', '02. CRO & Grants', '03. BPO (manuscription)'].forEach(itemLabel => {
                        const itemRow = updatedRows.find(r => r.label === itemLabel);
                        if (itemRow) {
                            itemRow.values?.forEach((val, idx) => {
                                researchTotalValues[idx] += Number(val) || 0;
                            });
                        }
                    });
                    // 연구 추가 항목들은 updatedRows에서 직접 찾아서 합산 (상태 비동기 문제 해결)
                    const fixedResearchLabels = ['01. Research Consulting', '02. CRO & Grants', '03. BPO (manuscription)', '연구부분'];
                    updatedRows.forEach(row => {
                        const rowInfo = getRowInfo(row);
                        // 연구 섹션에 속하고, 고정 항목이 아닌 경우 (추가 항목)
                        if (rowInfo.parentId === 'research' && row.label && !fixedResearchLabels.includes(row.label)) {
                            row.values?.forEach((val, idx) => {
                                researchTotalValues[idx] += Number(val) || 0;
                            });
                        }
                    });
                    updatedRows = updatedRows.map(row => {
                        if (row.label === '연구부분') {
                            return { ...row, values: researchTotalValues };
                        }
                        return row;
                    });

                    // 2. 총매출 재계산 (연구부분 + 컨설팅부분)
                    const 컨설팅부분Row = updatedRows.find(row => row.label === '컨설팅부분');
                    const totalRevenueValues = new Array(12).fill(0);
                    for (let i = 0; i < 12; i++) {
                        totalRevenueValues[i] = (researchTotalValues[i] || 0) + (Number(컨설팅부분Row?.values?.[i]) || 0);
                    }
                    updatedRows = updatedRows.map(row => {
                        if (row.label === '총 매출') {
                            return { ...row, values: totalRevenueValues };
                        }
                        return row;
                    });

                    // 3. 기말현금 재계산 (총매출 변경 반영)
                    const finalRows = recalculateEndingCash(updatedRows);

                    // 4. editableData에도 기말현금 반영
                    const 기말현금Row = finalRows.find(row => row.label === '기말현금');
                    const 기초현금Row = finalRows.find(row => row.label === '기초현금');
                    if (기말현금Row && 기초현금Row) {
                        setEditableData(prev => ({
                            ...prev,
                            '기말현금': 기말현금Row.values || new Array(12).fill(0),
                            '기초현금': 기초현금Row.values || new Array(12).fill(0)
                        }));
                    }

                    return { ...prev, rows: finalRows };
                }

                // 연구부분 외 항목 - 기존 로직 실행 (CF 직원 편집이 아닐 때만)
                const isCfEmployeeEdit = isEditing.sectionId === 'b-2' && cfAdditionalEmployees.some(emp => emp.name === isEditing.rowLabel);
                if (!isCfEmployeeEdit) {
                    // 1. 연구부분 총계 재계산
                    const 연구부분Row = updatedRows.find(row => row.label === '연구부분');
                    if (연구부분Row) {
                        const researchTotalValues = new Array(12).fill(0);

                        // 고정 항목들 합계
                        ['01. Research Consulting', '02. CRO & Grants', '03. BPO (manuscription)'].forEach(itemLabel => {
                            const itemRow = updatedRows.find(r => r.label === itemLabel);
                            if (itemRow) {
                                itemRow.values?.forEach((val, idx) => {
                                    researchTotalValues[idx] += Number(val) || 0;
                                });
                            }
                        });

                        // 동적 추가 항목들 합계 (updatedRows에서 직접 찾아서 합산)
                        const fixedResearchLabels = ['01. Research Consulting', '02. CRO & Grants', '03. BPO (manuscription)', '연구부분'];
                        updatedRows.forEach(row => {
                            const rowInfo = getRowInfo(row);
                            if (rowInfo.parentId === 'research' && row.label && !fixedResearchLabels.includes(row.label)) {
                                row.values?.forEach((val, idx) => {
                                    researchTotalValues[idx] += Number(val) || 0;
                                });
                            }
                        });

                        updatedRows = updatedRows.map(row => {
                            if (row.label === '연구부분') {
                                return { ...row, values: researchTotalValues };
                            }
                            return row;
                        });
                    }

                    // 2. 총매출 재계산 (연구부분 + 컨설팅부분)
                    const 총매출Row = updatedRows.find(row => row.label === '총 매출');
                    const 컨설팅부분Row = updatedRows.find(row => row.label === '컨설팅부분');
                    const updated연구부분Row = updatedRows.find(row => row.label === '연구부분');

                    if (총매출Row && 컨설팅부분Row && updated연구부분Row) {
                        const totalRevenueValues = new Array(12).fill(0);
                        for (let i = 0; i < 12; i++) {
                            totalRevenueValues[i] = (Number(updated연구부분Row.values?.[i]) || 0) + (Number(컨설팅부분Row.values?.[i]) || 0);
                        }

                        updatedRows = updatedRows.map(row => {
                            if (row.label === '총 매출') {
                                return { ...row, values: totalRevenueValues };
                            }
                            return row;
                        });
                    }

                    // 3. 모든 편집에서 지출합계 재계산
                    const 지출합계Row = updatedRows.find(row => row.label === '지출합계');
                    if (지출합계Row) {
                        const expenseTotalValues = new Array(12).fill(0);

                        // 지출합계 구성 요소들
                        const expenseComponents = [
                            'Annual Salary',
                            'Summer Bonus & Welfare',
                            'Project Expense',
                            'T & E Expense',
                            'Out sourcing & Restructuring'
                        ];

                        // 각 구성 요소의 값들을 합산
                        expenseComponents.forEach(componentLabel => {
                            const componentRow = updatedRows.find(r => r.label === componentLabel);
                            if (componentRow) {
                                componentRow.values?.forEach((val, idx) => {
                                    expenseTotalValues[idx] += Number(val) || 0;
                                });
                            }
                        });

                        // OPEX 데이터 추가 (화면에서 가져오기)
                        const indirectOpexRow = updatedRows.find(r => r.label === 'indirect opex');
                        const directOpexRow = updatedRows.find(r => r.label === 'direct opex');

                        if (indirectOpexRow) {
                            indirectOpexRow.values?.forEach((val: number, idx: number) => {
                                expenseTotalValues[idx] += Number(val) || 0;
                            });
                        }

                        if (directOpexRow) {
                            directOpexRow.values?.forEach((val: number, idx: number) => {
                                expenseTotalValues[idx] += Number(val) || 0;
                            });
                        }

                        updatedRows = updatedRows.map(row => {
                            if (row.label === '지출합계') {
                                return { ...row, values: expenseTotalValues };
                            }
                            return row;
                        });
                    }

                    // 4. b-3. 고정비 합계 재계산 (indirect opex + Summer Bonus & Welfare)
                    const indirectOpexRowForFixed = updatedRows.find(r => r.label === 'indirect opex');
                    const summerBonusRowForFixed = updatedRows.find(r => r.label === 'Summer Bonus & Welfare');
                    if (indirectOpexRowForFixed && summerBonusRowForFixed) {
                        const fixedCostValues = new Array(12).fill(0);
                        for (let i = 0; i < 12; i++) {
                            fixedCostValues[i] = (Number(indirectOpexRowForFixed.values?.[i]) || 0) +
                                                 (Number(summerBonusRowForFixed.values?.[i]) || 0);
                        }
                        updatedRows = updatedRows.map(row => {
                            if (row.label === 'b-3. 고정비') {
                                return { ...row, values: fixedCostValues };
                            }
                            return row;
                        });
                    }

                    // 5. b-4. 변동비 합계 재계산 (Project Expense + direct opex + T & E Expense)
                    const projectExpenseRowForVar = updatedRows.find(r => r.label === 'Project Expense');
                    const directOpexRowForVar = updatedRows.find(r => r.label === 'direct opex');
                    const teExpenseRowForVar = updatedRows.find(r => r.label === 'T & E Expense');
                    if (projectExpenseRowForVar && directOpexRowForVar && teExpenseRowForVar) {
                        const variableCostValues = new Array(12).fill(0);
                        for (let i = 0; i < 12; i++) {
                            variableCostValues[i] = (Number(projectExpenseRowForVar.values?.[i]) || 0) +
                                                    (Number(directOpexRowForVar.values?.[i]) || 0) +
                                                    (Number(teExpenseRowForVar.values?.[i]) || 0);
                        }
                        updatedRows = updatedRows.map(row => {
                            if (row.label === 'b-4. 변동비') {
                                return { ...row, values: variableCostValues };
                            }
                            return row;
                        });
                    }

                    // 기말현금은 편집 불가능, 항상 자동 계산
                    let finalRows;
                    if (isEditing.rowLabel === '기말현금') {
                        // 기말현금 편집 시도는 무시 (실제로는 편집 불가능하게 막아야 함)
                        setIsEditing(null);
                        setEditValue('');
                        return prev; // 변경사항 없이 이전 상태 반환
                    } else {
                        // 다른 편집: 1~12월 모두 재계산
                        finalRows = recalculateEndingCash(updatedRows);
                    }

                    // 단순화된 데이터 동기화 로직
                    const 기말현금Row = finalRows.find(row => row.label === '기말현금');
                    const 기초현금Row = finalRows.find(row => row.label === '기초현금');

                    if (기말현금Row && 기초현금Row) {
                        // editableData 업데이트 (재계산된 값들 반영)
                        setEditableData(prev => ({
                            ...prev,
                            '기말현금': 기말현금Row.values || new Array(12).fill(0),
                            '기초현금': 기초현금Row.values || new Array(12).fill(0)
                        }));

                        // 재계산 함수에서 이미 처리되었으므로 중복 로직 제거

                        // editableData의 모든 배열 필드를 안전하게 처리
                        const sanitizedEditableData = Object.fromEntries(
                            Object.entries(editableData).map(([key, val]) => [key, sanitizeNumberArray(val)])
                        );

                        // 편집 시 화면만 업데이트, 저장은 "확정" 버튼으로만 수행
                    }

                    return {
                        ...prev,
                        rows: finalRows
                    };
                }

                return {
                    ...prev,
                    rows: updatedRows
                };
            });

            setIsEditing(null);
            setEditValue('');

            // 중복 저장 방지를 위해 triggerEndingCashRecalculation 호출 제거
            // 대신 handleEditSave 내부의 기존 재계산 로직에 의존
        }
    };

    // 편집 취소
    const handleEditCancel = () => {
        setIsEditing(null);
        setEditValue('');
    };

    // 엔터키 처리
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleEditSave();
        } else if (e.key === 'Escape') {
            handleEditCancel();
        }
    };

    if (loading) {
        return (
            <div className="p-6 lg:p-8">
                <div className="flex items-center justify-center h-64">
                    <div className="text-slate-500">Cash Flow 데이터를 불러오는 중...</div>
                </div>
            </div>
        );
    }
    
    // ===== 행 정보 계산 함수 (새 UI용) =====
    const getRowInfo = (row: RowData): {
        level: 1 | 2 | 3;
        displayLabel: string;
        parentId: string | null;
        isCollapsible: boolean;
        sectionId: string | null;
    } => {
        const label = row.label || '';

        // Level 1: 대분류 (A. 현금흐름, B. 비용 총계, C. 매출 총계)
        if (label === 'A. 현금흐름') {
            return { level: 1, displayLabel: 'A. 현금흐름', parentId: null, isCollapsible: true, sectionId: 'A' };
        }
        if (label === '지출합계') {
            return { level: 1, displayLabel: 'B. 비용 총계', parentId: null, isCollapsible: true, sectionId: 'B' };
        }
        if (label === '총 매출') {
            return { level: 1, displayLabel: 'C. 매출 총계', parentId: null, isCollapsible: true, sectionId: 'C' };
        }

        // Level 2: 중분류
        if (label === '기말현금') {
            return { level: 2, displayLabel: 'a-1. 기말현금', parentId: 'A', isCollapsible: false, sectionId: null };
        }
        if (label === '기초현금') {
            return { level: 2, displayLabel: 'a-2. 기초현금', parentId: 'A', isCollapsible: false, sectionId: null };
        }
        if (label === 'Annual Salary') {
            return { level: 2, displayLabel: 'b-1. 인건비', parentId: 'B', isCollapsible: true, sectionId: 'b-1' };
        }
        if (label === 'Out sourcing & Restructuring') {
            return { level: 2, displayLabel: 'b-2. 외주인건비', parentId: 'B', isCollapsible: true, sectionId: 'b-2' };
        }
        if (label === 'b-3. 고정비') {
            return { level: 2, displayLabel: 'b-3. 고정비', parentId: 'B', isCollapsible: true, sectionId: 'b-3' };
        }
        if (label === 'b-4. 변동비') {
            return { level: 2, displayLabel: 'b-4. 변동비', parentId: 'B', isCollapsible: true, sectionId: 'b-4' };
        }
        if (label === '연구부분') {
            return { level: 2, displayLabel: '연구 사업부', parentId: 'C', isCollapsible: true, sectionId: 'research' };
        }
        if (label === '컨설팅부분') {
            return { level: 2, displayLabel: '컨설팅 사업부', parentId: 'C', isCollapsible: true, sectionId: 'consulting' };
        }

        // Level 3: 소분류 (상세 항목들)
        if (label === 'indirect opex') {
            return { level: 3, displayLabel: '법인운영비용', parentId: 'b-3', isCollapsible: false, sectionId: null };
        }
        if (label === 'Summer Bonus & Welfare') {
            return { level: 3, displayLabel: '복지비용', parentId: 'b-3', isCollapsible: false, sectionId: null };
        }
        if (label === 'Project Expense') {
            return { level: 3, displayLabel: '프로젝트 운영비용', parentId: 'b-4', isCollapsible: false, sectionId: null };
        }
        if (label === 'direct opex') {
            return { level: 3, displayLabel: '파트너 비용', parentId: 'b-4', isCollapsible: false, sectionId: null };
        }
        if (label === 'T & E Expense') {
            return { level: 3, displayLabel: '출장비', parentId: 'b-4', isCollapsible: false, sectionId: null };
        }
        if (['01. Research Consulting', '02. CRO & Grants', '03. BPO (manuscription)'].includes(label)) {
            // 동적 표시명 사용
            const displayLabel = researchDisplayLabels[RESEARCH_KEY_MAP[label]] || getDisplayLabel(label);
            return { level: 3, displayLabel, parentId: 'research', isCollapsible: false, sectionId: null };
        }

        // 직원 행 (b-1. 인건비 하위)
        if (row.type === 'item' && row.indent === 1) {
            // 행 자체에 sectionId가 있으면 우선 사용 (같은 이름의 항목 구분용)
            if (row.sectionId) {
                return { level: 3, displayLabel: label, parentId: row.sectionId, isCollapsible: false, sectionId: null };
            }
            // 연구 추가 항목인지 확인
            if (researchAdditionalItems.some(item => item.label === label)) {
                return { level: 3, displayLabel: label, parentId: 'research', isCollapsible: false, sectionId: null };
            }
            // 컨설팅 프로젝트인지 확인
            if (consultingDynamicProjects[label]) {
                return { level: 3, displayLabel: label, parentId: 'consulting', isCollapsible: false, sectionId: null };
            }
            // CF 추가 직원인지 확인 (외주인건비 하위)
            if (cfAdditionalEmployees.some(emp => emp.name === label)) {
                return { level: 3, displayLabel: label, parentId: 'b-2', isCollapsible: false, sectionId: null };
            }
            // 기본: 직원 (인건비 하위)
            return { level: 3, displayLabel: label, parentId: 'b-1', isCollapsible: false, sectionId: null };
        }

        return { level: 3, displayLabel: label, parentId: null, isCollapsible: false, sectionId: null };
    };

    // ===== 엑셀 다운로드 함수 =====
    const exportToExcel = async () => {
        const months = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Cash Flow');

        // 열 너비 설정
        worksheet.columns = [
            { width: 28 }, // 항목
            { width: 14 }, // 소계
            ...months.map(() => ({ width: 12 })) // 월별
        ];

        // 헤더 행 스타일
        const headerRow = worksheet.addRow(['항목', '소계', ...months]);
        headerRow.height = 24;
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }; // slate-700
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = {
                top: { style: 'thin', color: { argb: 'FF94A3B8' } },
                bottom: { style: 'thin', color: { argb: 'FF94A3B8' } },
                left: { style: 'thin', color: { argb: 'FF94A3B8' } },
                right: { style: 'thin', color: { argb: 'FF94A3B8' } }
            };
        });

        // 데이터 행 생성
        dynamicFinancialData.rows.forEach(row => {
            if (row.type === 'spacer') return;

            const { level, displayLabel } = getRowInfo(row);
            const values = row.values || new Array(12).fill(0);
            const subtotal = values.reduce((sum, val) => sum + (val || 0), 0);

            // 기말현금 값 (A. 현금흐름 행에서 월별 마이너스 경고 표시용)
            const 기말현금RowForExcel = dynamicFinancialData.rows.find(r => r.label === '기말현금');
            const 기말현금ValuesForExcel = 기말현금RowForExcel?.values || new Array(12).fill(0);

            // 만원 단위로 변환
            const subtotalValue = Math.round(subtotal / 10000);
            const monthlyValues = values.map((v, i) => {
                // A. 현금흐름: 기말현금이 마이너스인 월에만 경고 표시
                if (row.label === 'A. 현금흐름') {
                    return 기말현금ValuesForExcel[i] < 0 ? '⚠️' : '';
                }
                return Math.round((v || 0) / 10000);
            });

            // A. 현금흐름, 기말현금, 기초현금은 소계 표시 안함
            const displaySubtotal = (row.label === 'A. 현금흐름' || row.label === '기말현금' || row.label === '기초현금') ? '' : subtotalValue;
            const dataRow = worksheet.addRow([displayLabel, displaySubtotal, ...monthlyValues]);
            dataRow.height = 22;

            // 레벨별 스타일 적용
            let bgColor: string;
            let fontBold: boolean;
            let fontSize: number;
            let indentLevel: number;

            if (level === 1) {
                bgColor = 'FFF1F5F9'; // slate-100
                fontBold = true;
                fontSize = 11;
                indentLevel = 0;
            } else if (level === 2) {
                bgColor = 'FFF8FAFC'; // slate-50
                fontBold = true;
                fontSize = 10;
                indentLevel = 1;
            } else {
                bgColor = 'FFFFFFFF'; // white
                fontBold = false;
                fontSize = 10;
                indentLevel = 2;
            }

            dataRow.eachCell((cell, colNumber) => {
                // 배경색 적용
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };

                // 테두리 적용
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFE2E8F0' } }, // slate-200
                    bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                    left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                    right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
                };

                if (colNumber === 1) {
                    // 항목명 셀 (왼쪽 정렬 + 들여쓰기)
                    cell.font = { bold: fontBold, size: fontSize };
                    cell.alignment = { horizontal: 'left', vertical: 'middle', indent: indentLevel * 2 };
                } else {
                    // 숫자 셀 (오른쪽 정렬 + 천단위 구분)
                    const numValue = cell.value as number;
                    cell.font = {
                        bold: fontBold,
                        size: fontSize,
                        color: numValue < 0 ? { argb: 'FFDC2626' } : { argb: 'FF000000' } // 음수는 빨간색
                    };
                    cell.alignment = { horizontal: 'right', vertical: 'middle' };
                    cell.numFmt = '#,##0';
                }
            });
        });

        // 파일 다운로드
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `CashFlow_${selectedYear}.xlsx`);
    };

    // ===== 행이 접힌 상태로 숨겨져야 하는지 확인 =====
    const isRowHidden = (row: RowData): boolean => {
        const { parentId } = getRowInfo(row);
        if (!parentId) return false;

        // 부모가 접혀있으면 숨김
        if (collapsedSections.has(parentId)) return true;

        // 조부모도 확인 (예: 직원은 b-1 → B 순으로 확인)
        const parentRow = dynamicFinancialData.rows.find(r => {
            const info = getRowInfo(r);
            return info.sectionId === parentId;
        });
        if (parentRow) {
            const parentInfo = getRowInfo(parentRow);
            if (parentInfo.parentId && collapsedSections.has(parentInfo.parentId)) return true;
        }

        return false;
    };

    // ===== 새로운 renderRow 함수 (단일 항목 컬럼 + 들여쓰기) =====
    const renderRow = (row: RowData, index: number) => {
        // Spacer 처리
        if (row.type === 'spacer') {
            return <tr key={index} className="h-2"><td colSpan={14} className="bg-slate-100 border-y border-slate-300"></td></tr>;
        }

        // 숨겨진 행 체크
        if (isRowHidden(row)) return null;

        const { level, displayLabel, isCollapsible, sectionId, parentId } = getRowInfo(row);
        const isCollapsed = sectionId ? collapsedSections.has(sectionId) : false;

        // 값 처리
        const values = row.values || new Array(12).fill(0);
        const paddedValues = [...values];
        while (paddedValues.length < 12) paddedValues.push(0);
        const subtotal = calculateSubtotal(paddedValues);

        // 기말현금 값 (A. 현금흐름 행에서 월별 마이너스 경고 표시용)
        const 기말현금Row = dynamicFinancialData.rows.find(r => r.label === '기말현금');
        const 기말현금Values = 기말현금Row?.values || new Array(12).fill(0);

        // 들여쓰기 (레벨별 4칸씩)
        const indentPx = level === 1 ? 0 : level === 2 ? 16 : 32;

        // 스타일 결정
        const rowBgClass = level === 1 ? 'bg-slate-100' : level === 2 ? 'bg-slate-50' : 'bg-white';
        const fontClass = level === 1 ? 'font-bold' : level === 2 ? 'font-semibold' : 'font-normal';

        // 편집 가능 여부
        // Out sourcing & Restructuring 총계는 CF 직원 합계로 자동 계산 (직접 편집 불가)
        const isEditableRow = row.label && (
            ['Summer Bonus & Welfare', 'T & E Expense', '기말현금', '기초현금'].includes(row.label) ||
            researchAdditionalItems.some(item => item.label === row.label) ||
            ['01. Research Consulting', '02. CRO & Grants', '03. BPO (manuscription)'].includes(row.label) ||
            cfAdditionalEmployees.some(emp => emp.name === row.label)  // CF 직원은 편집 가능
        );

        // 퇴사자 확인
        let isResignedEmployee = false;
        if (row.type === 'item' && row.label) {
            const employee = employees.find((emp: any) => emp.name === row.label);
            if (employee && (employee.status === 'RESIGNED' || employee.status === 'INACTIVE' || employee.endDate)) {
                isResignedEmployee = true;
            }
        }

        // 버튼 렌더링 함수
        const renderActionButtons = () => {
            if (row.label === '연구부분') {
                return (
                    <button
                        onClick={async () => {
                            const itemName = prompt('새 연구 항목 이름을 입력하세요:');
                            if (itemName?.trim()) {
                                const trimmedName = itemName.trim();

                                // 중복 체크 1: 연구 추가 항목 내 중복 확인
                                const existingAdditionalItem = researchAdditionalItems.find(item => item.label === trimmedName);
                                if (existingAdditionalItem) {
                                    showToast('이미 같은 이름의 연구 항목이 존재합니다.', 'error');
                                    return;
                                }

                                // 중복 체크 2: 연구 고정 항목과 중복 확인
                                const fixedLabels = ['01. Research Consulting', '02. CRO & Grants', '03. BPO (manuscription)'];
                                if (fixedLabels.includes(trimmedName)) {
                                    showToast('고정 연구 항목과 같은 이름은 사용할 수 없습니다.', 'error');
                                    return;
                                }

                                const newItem = { label: trimmedName, values: new Array(12).fill(0) };
                                const updatedItems = [...researchAdditionalItems, newItem];
                                setResearchAdditionalItems(updatedItems);
                                try {
                                    await generateDynamicData(updatedItems, editableData, researchItems, null);
                                    await saveCashFlowData(editableData, researchItems, updatedItems);
                                    showToast('항목이 추가되었습니다.', 'success');
                                    setTimeout(() => reloadWithYear(), 100);
                                } catch { showToast('항목 추가에 실패했습니다.', 'error'); }
                            }
                        }}
                        className="ml-2 px-2 py-0.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                    >+ 추가</button>
                );
            }
            if (row.label === 'Out sourcing & Restructuring') {
                return <button onClick={addCfEmployee} className="ml-2 px-2 py-0.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">+ 추가</button>;
            }
            // CF 직원 삭제 버튼 (b-2 섹션에서만)
            if (parentId === 'b-2' && row.label && cfAdditionalEmployees.some(emp => emp.name === row.label)) {
                return <button onClick={() => removeCfEmployee(row.label || '')} className="ml-2 px-1 py-0.5 text-xs bg-red-500 text-white rounded hover:bg-red-600">삭제</button>;
            }
            // 연구 추가 항목 삭제 버튼 (research 섹션에서만)
            if (parentId === 'research' && researchAdditionalItems.some(item => item.label === row.label)) {
                return (
                    <button
                        onClick={async () => {
                            const updatedItems = researchAdditionalItems.filter(item => item.label !== row.label);
                            setResearchAdditionalItems(updatedItems);
                            try {
                                await generateDynamicData(updatedItems, editableData, researchItems, null);
                                await saveCashFlowData(editableData, researchItems, updatedItems);
                                showToast('항목이 삭제되었습니다.', 'success');
                                setTimeout(() => reloadWithYear(), 100);
                            } catch { showToast('삭제에 실패했습니다.', 'error'); }
                        }}
                        className="ml-2 px-1 py-0.5 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                    >삭제</button>
                );
            }
            return null;
        };

        return (
            <tr key={index} className={`${rowBgClass} hover:bg-blue-50/50 transition-colors border-b border-slate-200`}>
                {/* 항목 컬럼 (단일 컬럼 + 들여쓰기) */}
                <td className={`px-3 py-2 border-r border-slate-200 text-sm ${fontClass} ${isResignedEmployee ? 'text-gray-400' : 'text-slate-800'}`}>
                    <div className="flex items-center" style={{ paddingLeft: `${indentPx}px` }}>
                        {isCollapsible && sectionId ? (
                            <button onClick={() => toggleSection(sectionId)} className="mr-1 w-4 h-4 flex items-center justify-center text-slate-500 hover:text-slate-700 text-xs">
                                {isCollapsed ? '▶' : '▼'}
                            </button>
                        ) : <span className="mr-1 w-4" />}
                        {/* 연구 고정 항목 라벨은 클릭하여 수정 가능 */}
                        {['01. Research Consulting', '02. CRO & Grants', '03. BPO (manuscription)'].includes(row.label || '') ? (() => {
                            const key = RESEARCH_KEY_MAP[row.label!];
                            const isEditingThisLabel = editingLabelKey === key;

                            return isEditingThisLabel ? (
                                <input
                                    type="text"
                                    value={editingLabelValue}
                                    onChange={(e) => setEditingLabelValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            if (editingLabelValue.trim()) {
                                                setResearchDisplayLabels(prev => ({
                                                    ...prev,
                                                    [key]: editingLabelValue.trim()
                                                }));
                                            }
                                            setEditingLabelKey(null);
                                            setEditingLabelValue('');
                                        } else if (e.key === 'Escape') {
                                            setEditingLabelKey(null);
                                            setEditingLabelValue('');
                                        }
                                    }}
                                    onBlur={() => {
                                        if (editingLabelValue.trim()) {
                                            setResearchDisplayLabels(prev => ({
                                                ...prev,
                                                [key]: editingLabelValue.trim()
                                            }));
                                        }
                                        setEditingLabelKey(null);
                                        setEditingLabelValue('');
                                    }}
                                    className="w-24 px-1 py-0 text-sm border border-blue-400 rounded outline-none focus:ring-1 focus:ring-blue-400"
                                    autoFocus
                                />
                            ) : (
                                <span
                                    className={`cursor-pointer hover:text-blue-600 hover:underline ${isResignedEmployee ? 'line-through' : ''}`}
                                    title="클릭하여 표시명 수정"
                                    onClick={() => {
                                        setEditingLabelKey(key);
                                        setEditingLabelValue(researchDisplayLabels[key]);
                                    }}
                                >
                                    {displayLabel}
                                </span>
                            );
                        })() : (
                            <span className={isResignedEmployee ? 'line-through' : ''}>{displayLabel}</span>
                        )}
                        {renderActionButtons()}
                    </div>
                </td>

                {/* 소계 컬럼 */}
                <td className={`px-2 py-2 border-r border-slate-200 text-right ${level === 3 ? 'text-xs text-slate-500' : `text-sm ${fontClass}`} ${subtotal < 0 ? 'text-red-600' : level === 3 ? '' : 'text-slate-700'}`}>
                    {/* A. 현금흐름, 기말현금, 기초현금은 소계 표시 안함 */}
                    {row.label === 'A. 현금흐름' || row.label === '기말현금' || row.label === '기초현금' ? '' : formatCurrency(subtotal)}
                </td>

                {/* 12개월 데이터 컬럼 */}
                {paddedValues.map((val, i) => {
                    const employeeName = (row.type === 'item' && row.label) ? row.label : '';
                    const combinedEmployee = Object.values(combinedHRCostData || {}).find((data: any) => data?.employee?.name === employeeName);
                    let isResignedCell = false;
                    if (row.type === 'item' && employeeName && combinedEmployee?.employee) {
                        isResignedCell = isResignedEmployeeInactiveMonth(combinedEmployee.employee, selectedYear, i + 1);
                    }

                    const isCashFlowEditableCell = isEditableRow && (() => {
                        if (row.label === '기말현금') return false;
                        if (row.label === '기초현금') return i === 0;
                        return true;
                    })();

                    const isCurrentEditing = isEditing && isEditing.rowLabel === row.label && isEditing.monthIndex === i && isEditing.sectionId === parentId;
                    const isReadOnlyCell = row.label === '기말현금' || (row.label === '기초현금' && i > 0);

                    // 기초현금은 마이너스여도 빨간색 표시 안함
                    const showRedText = val < 0 && row.label !== '기초현금' && row.label !== 'A. 현금흐름';

                    return (
                        <td key={i}
                            className={`px-2 py-2 border-r border-slate-200 text-right text-xs
                                ${showRedText ? 'text-red-600 font-medium' : isResignedEmployee ? 'text-gray-400' : 'text-slate-700'}
                                ${isCashFlowEditableCell ? 'cursor-pointer hover:bg-blue-100' : ''}
                                ${isCurrentEditing ? 'bg-blue-100 ring-2 ring-blue-400' : ''}
                                ${isResignedCell ? 'bg-yellow-100' : ''}
                                ${isReadOnlyCell ? 'bg-gray-50' : ''}`}
                            onClick={isCashFlowEditableCell ? () => handleEditableClick(row.label!, i, val / 10000, parentId) : undefined}
                        >
                            {isCurrentEditing ? (
                                <input
                                    type="number"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    onBlur={() => handleEditSave()}
                                    className="w-20 text-right text-xs border border-blue-400 rounded outline-none bg-white px-2 py-0.5 focus:ring-1 focus:ring-blue-400 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                                    placeholder="만원"
                                    autoFocus
                                />
                            ) : row.label === 'A. 현금흐름' ? (
                                // A. 현금흐름: 기말현금이 마이너스인 월에만 빨간 경고 아이콘 표시
                                기말현금Values[i] < 0
                                    ? <span title={`기말현금: ${formatCurrency(기말현금Values[i])}`} className="inline-flex justify-center">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M10.29 3.86L1.82 18C1.645 18.3024 1.55296 18.6453 1.55296 18.9945C1.55296 19.3437 1.645 19.6866 1.82 19.989C1.995 20.2914 2.246 20.5424 2.548 20.7174C2.85 20.8924 3.1929 20.9844 3.542 20.9844H20.458C20.8071 20.9844 21.15 20.8924 21.452 20.7174C21.754 20.5424 22.005 20.2914 22.18 19.989C22.355 19.6866 22.447 19.3437 22.447 18.9945C22.447 18.6453 22.355 18.3024 22.18 18L13.71 3.86C13.535 3.55761 13.284 3.30661 12.982 3.13161C12.68 2.95661 12.3371 2.86456 11.988 2.86456C11.639 2.86456 11.296 2.95661 10.994 3.13161C10.692 3.30661 10.441 3.55761 10.266 3.86H10.29Z" fill="#FF3B30"/>
                                            <path d="M12 9V13M12 17H12.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      </span>
                                    : ''
                            ) : formatCurrency(val)}
                        </td>
                    );
                })}
            </tr>
        );
    };

    return (
        <div className="p-6 lg:p-8">
            <header className="mb-8">
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-800">
                            Cash Flow
                        </h1>
                        <p className="text-slate-500 mt-1">현금 흐름 분석 및 재무 요약</p>
                    </div>
                    {/* 연도 선택기 */}
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-slate-700">연도:</label>
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                                className="px-3 py-1 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {availableYears.filter(year => year >= CASH_FLOW_START_YEAR).map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            onClick={exportToExcel}
                            className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                            엑셀
                        </button>
                        <button
                            onClick={async () => {
                                if (isSaving) return; // 저장 중이면 무시

                                setIsSaving(true);
                                try {
                                    // Cash Flow 데이터 전체 저장 (CF 직원 포함)
                                    await saveCashFlowData();

                                    showToast('저장되었습니다.', 'success');
                                    reloadWithYear();
                                } catch (error) {
                                    showToast('저장에 실패했습니다.', 'error');
                                } finally {
                                    setIsSaving(false);
                                }
                            }}
                            disabled={isSaving}
                            className={`px-4 py-1.5 text-white text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                isSaving
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                        >
                            {isSaving ? '저장 중...' : '확정'}
                        </button>
                    </div>
                </div>
            </header>

            {/* 메인 Cash Flow 테이블 - 계층적 단일 컬럼 구조 */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-slate-200">
                                <th className="px-3 py-2 border border-slate-300 text-center text-sm font-bold text-slate-800 w-48">
                                    항목
                                </th>
                                <th className="px-3 py-2 border border-slate-300 text-center text-sm font-bold text-slate-800 w-24">
                                    소계
                                </th>
                                {['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'].map(m => (
                                    <th key={m} className="px-2 py-2 border border-slate-300 text-center text-sm font-bold text-slate-800 w-20">{m}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {dynamicFinancialData.rows.map(renderRow)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CashFlow;
